import { useState, useEffect, useCallback, useMemo } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { Sidebar } from "../components/layout/Sidebar";
import { useSidebar } from "../contexts/SidebarContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Checkbox } from "../components/ui/checkbox";
import { toast } from "sonner";
import { useEditor, EditorContent } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Underline from "@tiptap/extension-underline";
import { jsPDF } from "jspdf";
import { Document, Packer, Paragraph, TextRun } from "docx";
import { saveAs } from "file-saver";
import SignatureFieldPlacer from "../components/SignatureFieldPlacer";
import {
  FileSignature,
  Plus,
  FileText,
  Download,
  Copy,
  CheckCircle,
  XCircle,
  AlertTriangle,
  Trash2,
  Edit3,
  Save,
  Bold,
  Italic,
  Underline as UnderlineIcon,
  List,
  ListOrdered,
  Heading1,
  Heading2,
  Lock,
  RefreshCw,
  Clock,
  History,
  Eye,
  ChevronRight,
  Send,
  Mail,
  Users,
  ExternalLink,
  Loader2,
  Printer,
  Upload,
  FileUp,
  PenLine,
  MousePointer,
  Sparkles,
  X
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Contract Templates with EMD and Inspection sections
const CONTRACT_TEMPLATES = {
  purchase_agreement: {
    name: "Purchase Agreement",
    description: "Standard real estate purchase agreement",
    sections: ["parties", "property", "price", "earnest_money", "inspection", "closing", "signatures"],
    template: `
<h1>REAL ESTATE PURCHASE AGREEMENT</h1>

<h2>1. PARTIES</h2>
<p>This Real Estate Purchase Agreement ("Agreement") is entered into as of <strong>{{closing_date}}</strong> by and between:</p>
<p><strong>SELLER:</strong> {{seller_name}}<br/>
<strong>Address:</strong> {{seller_address}}</p>
<p><strong>BUYER:</strong> {{buyer_name}}<br/>
<strong>Address:</strong> {{buyer_address}}</p>

<h2>2. PROPERTY</h2>
<p>Seller agrees to sell and Buyer agrees to purchase the following described real property:</p>
<p><strong>Property Address:</strong> {{property_address}}</p>
<p><strong>Legal Description:</strong> {{legal_description}}</p>

<h2>3. PURCHASE PRICE AND PAYMENT</h2>
<p><strong>Purchase Price:</strong> \${{purchase_price}}</p>
<p><strong>Balance Due at Closing:</strong> \${{balance_due}}</p>

<h2>4. EARNEST MONEY DEPOSIT</h2>
<p><strong>Earnest Money Amount:</strong> \${{earnest_money}}</p>
<p><strong>Deposit Due Within:</strong> {{earnest_days}} business days of execution</p>
<p><strong>Settlement Agent:</strong> {{settlement_agent}}</p>
<p>The earnest money shall be held by settlement agent and applied toward the purchase price at closing. If Buyer defaults, Seller may retain earnest money as liquidated damages.</p>

<h2>5. INSPECTION PERIOD</h2>
<p><strong>Inspection Period Length:</strong> {{inspection_days}} days</p>
<p><strong>Inspection Start Date:</strong> {{inspection_start_date}}</p>
<p><strong>Inspection End Date:</strong> {{inspection_end_date}}</p>
<p>Buyer shall have the right to conduct inspections of the property during the inspection period. If Buyer is not satisfied with the results of any inspection, Buyer may terminate this Agreement by written notice to Seller before the end of the inspection period and receive a full refund of earnest money.</p>

<h2>6. CLOSING</h2>
<p><strong>Closing Date:</strong> {{closing_date}}</p>
<p><strong>Closing Location:</strong> {{closing_location}}</p>
<p>Seller shall deliver a general warranty deed at closing.</p>

<h2>7. PROPERTY CONDITION</h2>
<p>Property is sold in "AS-IS" condition unless otherwise agreed in writing. Seller makes no warranties regarding the condition of the property except as required by law.</p>

<h2>8. SIGNATURES</h2>
<p>IN WITNESS WHEREOF, the parties have executed this Agreement as of the date first written above.</p>
<br/><br/>
<p>_________________________________<br/>
<strong>SELLER:</strong> {{seller_name}}<br/>
Date: _______________</p>
<br/>
<p>_________________________________<br/>
<strong>BUYER:</strong> {{buyer_name}}<br/>
Date: _______________</p>

<hr/>
<p><em>DISCLAIMER: This document is a draft generated from user-provided data and templates. It is not legal advice. Have a licensed attorney review before use.</em></p>
`
  },
  assignment_agreement: {
    name: "Assignment Agreement",
    description: "Contract assignment for wholesale deals",
    sections: ["parties", "original_contract", "assignment", "fee", "signatures"],
    template: `
<h1>ASSIGNMENT OF REAL ESTATE PURCHASE AGREEMENT</h1>

<h2>1. PARTIES</h2>
<p>This Assignment of Real Estate Purchase Agreement ("Assignment") is made as of <strong>{{assignment_date}}</strong> by and between:</p>
<p><strong>ASSIGNOR:</strong> {{assignor_name}}<br/>
<strong>Address:</strong> {{assignor_address}}</p>
<p><strong>ASSIGNEE:</strong> {{assignee_name}}<br/>
<strong>Address:</strong> {{assignee_address}}</p>

<h2>2. ORIGINAL CONTRACT</h2>
<p>Assignor entered into a Real Estate Purchase Agreement dated {{original_contract_date}} ("Original Contract") for the purchase of the following property:</p>
<p><strong>Property Address:</strong> {{property_address}}</p>
<p><strong>Original Purchase Price:</strong> \${{original_price}}</p>

<h2>3. ASSIGNMENT</h2>
<p>For good and valuable consideration, Assignor hereby assigns, transfers, and conveys to Assignee all of Assignor's right, title, and interest in and to the Original Contract.</p>

<h2>4. ASSIGNMENT FEE</h2>
<p><strong>Assignment Fee:</strong> \${{assignment_fee}}</p>
<p>Assignee agrees to pay the Assignment Fee to Assignor at closing.</p>
<p><strong>Assignee's Total Investment:</strong> \${{total_price}} (Original Price + Assignment Fee)</p>

<h2>5. EARNEST MONEY</h2>
<p><strong>Original Earnest Money:</strong> \${{original_earnest}} (held by {{settlement_agent}})</p>
<p><strong>Additional Earnest Money from Assignee:</strong> \${{additional_earnest}}</p>
<p>Assignee shall deposit additional earnest money within {{earnest_days}} days of execution.</p>

<h2>6. INSPECTION</h2>
<p><strong>Remaining Inspection Period:</strong> {{inspection_days}} days from assignment date</p>
<p>Assignee accepts assignment subject to remaining inspection rights under the Original Contract.</p>

<h2>7. CLOSING</h2>
<p><strong>Closing Date:</strong> {{closing_date}}</p>
<p>Assignee shall close directly with Seller per the terms of the Original Contract.</p>

<h2>8. SIGNATURES</h2>
<br/><br/>
<p>_________________________________<br/>
<strong>ASSIGNOR:</strong> {{assignor_name}}<br/>
Date: _______________</p>
<br/>
<p>_________________________________<br/>
<strong>ASSIGNEE:</strong> {{assignee_name}}<br/>
Date: _______________</p>

<hr/>
<p><em>DISCLAIMER: This document is a draft generated from user-provided data and templates. It is not legal advice. Have a licensed attorney review before use.</em></p>
`
  },
  seller_finance: {
    name: "Seller Finance Agreement",
    description: "Owner financing agreement with promissory note",
    sections: ["parties", "property", "terms", "payment", "default", "signatures"],
    template: `
<h1>SELLER FINANCING AGREEMENT</h1>

<h2>1. PARTIES</h2>
<p>This Seller Financing Agreement ("Agreement") is entered into as of <strong>{{closing_date}}</strong> by and between:</p>
<p><strong>SELLER/LENDER:</strong> {{seller_name}}<br/>
<strong>Address:</strong> {{seller_address}}</p>
<p><strong>BUYER/BORROWER:</strong> {{buyer_name}}<br/>
<strong>Address:</strong> {{buyer_address}}</p>

<h2>2. PROPERTY</h2>
<p><strong>Property Address:</strong> {{property_address}}</p>
<p><strong>Legal Description:</strong> {{legal_description}}</p>

<h2>3. EARNEST MONEY</h2>
<p><strong>Earnest Money Deposit:</strong> \${{earnest_money}}</p>
<p><strong>Due Within:</strong> {{earnest_days}} days</p>
<p><strong>Settlement Agent:</strong> {{settlement_agent}}</p>

<h2>4. INSPECTION PERIOD</h2>
<p><strong>Inspection Period:</strong> {{inspection_days}} days</p>
<p><strong>Start Date:</strong> {{inspection_start_date}}</p>
<p><strong>End Date:</strong> {{inspection_end_date}}</p>

<h2>5. FINANCING TERMS</h2>
<p><strong>Purchase Price:</strong> \${{purchase_price}}</p>
<p><strong>Down Payment:</strong> \${{down_payment}}</p>
<p><strong>Financed Amount:</strong> \${{loan_amount}}</p>
<p><strong>Interest Rate:</strong> {{interest_rate}}% per annum</p>
<p><strong>Amortization Period:</strong> {{amortization_years}} years</p>
<p><strong>Balloon Payment Due:</strong> {{balloon_date}} ({{balloon_years}} years from closing)</p>

<h2>6. PAYMENT TERMS</h2>
<p><strong>Monthly Payment:</strong> \${{monthly_payment}} (Principal & Interest)</p>
<p><strong>First Payment Due:</strong> {{first_payment_date}}</p>
<p><strong>Payment Due Date:</strong> {{payment_day}} of each month</p>
<p><strong>Balloon Payment Amount:</strong> \${{balloon_amount}} (estimated)</p>

<h2>7. SECURITY</h2>
<p>This Agreement shall be secured by a Deed of Trust/Mortgage on the Property. Buyer shall maintain hazard insurance with Seller named as mortgagee.</p>

<h2>8. DEFAULT</h2>
<p>If Buyer fails to make any payment when due, Seller may declare the entire balance immediately due and payable after {{grace_period}} days written notice.</p>

<h2>9. PREPAYMENT</h2>
<p>Buyer may prepay all or part of the principal without penalty.</p>

<h2>10. SIGNATURES</h2>
<br/><br/>
<p>_________________________________<br/>
<strong>SELLER/LENDER:</strong> {{seller_name}}<br/>
Date: _______________</p>
<br/>
<p>_________________________________<br/>
<strong>BUYER/BORROWER:</strong> {{buyer_name}}<br/>
Date: _______________</p>

<hr/>
<p><em>DISCLAIMER: This document is a draft generated from user-provided data and templates. It is not legal advice. Have a licensed attorney review before use.</em></p>
`
  },
  wrap_agreement: {
    name: "Wrap Mortgage Agreement",
    description: "Wraparound mortgage / All-inclusive deed of trust",
    sections: ["parties", "property", "underlying", "wrap_terms", "payments", "signatures"],
    template: `
<h1>WRAPAROUND MORTGAGE AGREEMENT</h1>
<p style="text-align: center;">(All-Inclusive Deed of Trust)</p>

<h2>1. PARTIES</h2>
<p>This Wraparound Mortgage Agreement ("Agreement") is entered into as of <strong>{{closing_date}}</strong> by and between:</p>
<p><strong>SELLER:</strong> {{seller_name}}<br/>
<strong>Address:</strong> {{seller_address}}</p>
<p><strong>BUYER:</strong> {{buyer_name}}<br/>
<strong>Address:</strong> {{buyer_address}}</p>

<h2>2. PROPERTY</h2>
<p><strong>Property Address:</strong> {{property_address}}</p>

<h2>3. EARNEST MONEY</h2>
<p><strong>Earnest Money:</strong> \${{earnest_money}}</p>
<p><strong>Due Within:</strong> {{earnest_days}} days to {{settlement_agent}}</p>

<h2>4. INSPECTION PERIOD</h2>
<p><strong>Inspection Period:</strong> {{inspection_days}} days</p>
<p><strong>Start:</strong> {{inspection_start_date}} | <strong>End:</strong> {{inspection_end_date}}</p>

<h2>5. UNDERLYING LOAN</h2>
<p>The Property is currently encumbered by an existing loan:</p>
<p><strong>Lender:</strong> {{underlying_lender}}</p>
<p><strong>Current Balance:</strong> \${{underlying_balance}}</p>
<p><strong>Interest Rate:</strong> {{underlying_rate}}%</p>
<p><strong>Monthly Payment:</strong> \${{underlying_payment}}</p>

<h2>6. WRAP LOAN TERMS</h2>
<p><strong>Sale Price:</strong> \${{sale_price}}</p>
<p><strong>Down Payment:</strong> \${{down_payment}}</p>
<p><strong>Wrap Loan Amount:</strong> \${{wrap_amount}}</p>
<p><strong>Wrap Interest Rate:</strong> {{wrap_rate}}%</p>
<p><strong>Amortization:</strong> {{wrap_amortization}} years</p>
<p><strong>Balloon Due:</strong> {{balloon_date}} ({{balloon_years}} years)</p>

<h2>7. PAYMENT TERMS</h2>
<p><strong>Buyer's Monthly Payment to Seller:</strong> \${{buyer_payment}}</p>
<p><strong>Seller's Payment on Underlying:</strong> \${{underlying_payment}}</p>
<p><strong>Monthly Spread to Seller:</strong> \${{monthly_spread}}</p>

<h2>8. SELLER'S OBLIGATIONS</h2>
<p>Seller agrees to:</p>
<ul>
<li>Continue making payments on the underlying loan</li>
<li>Maintain insurance on the property</li>
<li>Provide Buyer with proof of underlying payments upon request</li>
</ul>

<h2>9. DUE-ON-SALE DISCLOSURE</h2>
<p>BUYER ACKNOWLEDGES: The underlying loan may contain a due-on-sale clause that could be triggered by this transaction. Both parties understand and accept this risk.</p>

<h2>10. SIGNATURES</h2>
<br/><br/>
<p>_________________________________<br/>
<strong>SELLER:</strong> {{seller_name}}<br/>
Date: _______________</p>
<br/>
<p>_________________________________<br/>
<strong>BUYER:</strong> {{buyer_name}}<br/>
Date: _______________</p>

<hr/>
<p><em>DISCLAIMER: This document is a draft generated from user-provided data and templates. It is not legal advice. Have a licensed attorney review before use.</em></p>
`
  },
  lease_option: {
    name: "Lease Option Agreement",
    description: "Lease with option to purchase",
    sections: ["parties", "property", "lease", "option", "credits", "signatures"],
    template: `
<h1>LEASE WITH OPTION TO PURCHASE AGREEMENT</h1>

<h2>1. PARTIES</h2>
<p>This Lease with Option to Purchase Agreement ("Agreement") is entered into as of <strong>{{start_date}}</strong> by and between:</p>
<p><strong>LANDLORD/SELLER:</strong> {{seller_name}}<br/>
<strong>Address:</strong> {{seller_address}}</p>
<p><strong>TENANT/BUYER:</strong> {{buyer_name}}<br/>
<strong>Address:</strong> {{buyer_address}}</p>

<h2>2. PROPERTY</h2>
<p><strong>Property Address:</strong> {{property_address}}</p>

<h2>PART A: LEASE TERMS</h2>

<h3>3. LEASE PERIOD</h3>
<p><strong>Lease Start Date:</strong> {{start_date}}</p>
<p><strong>Lease End Date:</strong> {{end_date}}</p>
<p><strong>Lease Term:</strong> {{lease_months}} months</p>

<h3>4. RENT</h3>
<p><strong>Monthly Rent:</strong> \${{monthly_rent}}</p>
<p><strong>Security Deposit:</strong> \${{security_deposit}}</p>
<p><strong>Due Date:</strong> {{rent_due_day}} of each month</p>

<h2>PART B: OPTION TO PURCHASE</h2>

<h3>5. OPTION FEE</h3>
<p><strong>Option Consideration:</strong> \${{option_fee}}</p>
<p>This fee is non-refundable but will be credited toward purchase price if option is exercised.</p>

<h3>6. PURCHASE PRICE</h3>
<p><strong>Option Price:</strong> \${{purchase_price}}</p>
<p>This price is fixed for the duration of the option period.</p>

<h3>7. OPTION PERIOD</h3>
<p>Tenant/Buyer has the exclusive right to purchase the property at any time during the lease term by providing {{notice_days}} days written notice.</p>

<h3>8. INSPECTION</h3>
<p><strong>Initial Inspection Period:</strong> {{inspection_days}} days from lease start</p>
<p>Tenant may conduct inspections during this period. If not satisfied, Tenant may terminate and receive refund of option fee minus {{inspection_fee}} inspection fee.</p>

<h3>9. RENT CREDITS</h3>
<p><strong>Monthly Rent Credit:</strong> \${{rent_credit}}</p>
<p>{{rent_credit_percent}}% of each month's rent will be credited toward the purchase price if option is exercised.</p>
<p><strong>Maximum Rent Credit:</strong> \${{max_rent_credit}}</p>

<h3>10. CREDITS AT CLOSING</h3>
<ul>
<li>Option Fee: \${{option_fee}}</li>
<li>Rent Credits (if fully earned): \${{max_rent_credit}}</li>
<li><strong>Total Credits:</strong> \${{total_credits}}</li>
<li><strong>Balance Due at Closing:</strong> \${{balance_due}}</li>
</ul>

<h2>11. SIGNATURES</h2>
<br/><br/>
<p>_________________________________<br/>
<strong>LANDLORD/SELLER:</strong> {{seller_name}}<br/>
Date: _______________</p>
<br/>
<p>_________________________________<br/>
<strong>TENANT/BUYER:</strong> {{buyer_name}}<br/>
Date: _______________</p>

<hr/>
<p><em>DISCLAIMER: This document is a draft generated from user-provided data and templates. It is not legal advice. Have a licensed attorney review before use.</em></p>
`
  },
  investor_disclosure: {
    name: "Investor Disclosure Statement",
    description: "Professional disclosure for investment opportunities",
    sections: ["header", "opportunity_summary", "financial_projections", "risks", "disclaimers", "acknowledgment"],
    template: `
<h1 style="text-align: center;">PRIVATE INVESTMENT DISCLOSURE STATEMENT</h1>
<p style="text-align: center;"><em>Confidential — For Qualified Investors Only</em></p>

<hr/>

<h2>INVESTMENT OPPORTUNITY SUMMARY</h2>

<p><strong>Property Address:</strong> {{property_address}}</p>
<p><strong>Investment Type:</strong> {{investment_type}}</p>
<p><strong>Total Capital Required:</strong> \${{total_capital}}</p>
<p><strong>Target Hold Period:</strong> {{hold_period}} months</p>
<p><strong>Projected Return (ROI):</strong> {{projected_roi}}%</p>
<p><strong>Minimum Investment:</strong> \${{minimum_investment}}</p>

<h2>SPONSOR INFORMATION</h2>
<p><strong>Sponsor/Operator:</strong> {{sponsor_name}}</p>
<p><strong>Entity:</strong> {{sponsor_entity}}</p>
<p><strong>Contact:</strong> {{sponsor_contact}}</p>

<h2>INVESTMENT STRUCTURE</h2>
<p><strong>Equity Split:</strong> {{equity_split}}</p>
<p><strong>Preferred Return:</strong> {{preferred_return}}%</p>
<p><strong>Profit Participation:</strong> {{profit_participation}}</p>

<h2>USE OF FUNDS</h2>
<ul>
<li>Acquisition: \${{acquisition_cost}}</li>
<li>Renovation/Improvements: \${{renovation_cost}}</li>
<li>Carrying Costs: \${{carrying_costs}}</li>
<li>Reserves: \${{reserves}}</li>
</ul>

<h2>PROJECTED RETURNS</h2>
<p><strong>After Repair Value (ARV):</strong> \${{arv}}</p>
<p><strong>Less: Selling Costs (8%):</strong> (\${{selling_costs}})</p>
<p><strong>Less: Total Investment:</strong> (\${{total_investment}})</p>
<p><strong>Projected Net Profit:</strong> \${{projected_profit}}</p>

<h2>RISK FACTORS</h2>
<p><strong>Investors should carefully consider the following risks before investing:</strong></p>
<ol>
<li><strong>Market Risk:</strong> Real estate values may decline due to market conditions, economic factors, or local market changes.</li>
<li><strong>Execution Risk:</strong> Renovation costs may exceed estimates, timelines may extend, or unforeseen issues may arise.</li>
<li><strong>Liquidity Risk:</strong> Real estate investments are illiquid. Investors may not be able to exit their investment on demand.</li>
<li><strong>Financing Risk:</strong> Interest rates may change, or financing may not be available on anticipated terms.</li>
<li><strong>Loss of Capital:</strong> This investment involves substantial risk including the potential loss of all invested capital.</li>
</ol>

<h2>DISCLAIMERS</h2>
<p><em>This document is provided for informational purposes only and does not constitute an offer to sell or a solicitation of an offer to buy any securities. Any such offer or solicitation will be made only by means of a formal offering document. Past performance is not indicative of future results.</em></p>

<h2>INVESTOR ACKNOWLEDGMENT</h2>
<p>By signing below, I acknowledge that I have received and reviewed this Disclosure Statement, I understand the risks associated with this investment, and I have had the opportunity to ask questions.</p>

<br/><br/>
<p>_________________________________<br/>
<strong>INVESTOR NAME:</strong> {{investor_name}}<br/>
<strong>DATE:</strong> _______________</p>

<br/>
<p>_________________________________<br/>
<strong>SPONSOR:</strong> {{sponsor_name}}<br/>
<strong>DATE:</strong> _______________</p>

<hr/>
<p><em>Generated by DealForge — The Transaction Operating System</em></p>
`
  },
  letter_of_intent: {
    name: "Letter of Intent (LOI)",
    description: "Non-binding expression of interest to purchase",
    sections: ["header", "parties", "terms", "conditions", "timeline", "signatures"],
    template: `
<p style="text-align: right;"><strong>{{buyer_entity}}</strong><br/>
{{buyer_address}}<br/>
{{buyer_phone}}<br/>
{{buyer_email}}</p>
<p style="text-align: right;"><strong>Date:</strong> {{loi_date}}</p>

<p><strong>{{seller_name}}</strong><br/>
{{seller_address}}</p>

<p><strong>RE: Letter of Intent — {{property_address}}</strong></p>

<p>Dear {{seller_name}},</p>

<p>This Letter of Intent ("LOI") sets forth the principal terms upon which <strong>{{buyer_name}}</strong> ("Buyer") is prepared to acquire the property located at <strong>{{property_address}}</strong> ("Property"). This LOI is intended as a framework for negotiation and does not create any binding obligation between the parties.</p>

<h2>1. PURCHASE PRICE</h2>
<p><strong>Offered Purchase Price:</strong> \${{purchase_price}}</p>
<p><strong>Earnest Money Deposit:</strong> \${{earnest_money}}</p>
<p><strong>Payment Method:</strong> {{payment_method}}</p>

<h2>2. DUE DILIGENCE</h2>
<p><strong>Inspection Period:</strong> {{inspection_days}} days from execution of Purchase Agreement</p>
<p>During this period, Buyer shall have the right to conduct physical inspections, environmental assessments, title examination, and review of all property records.</p>

<h2>3. CLOSING</h2>
<p><strong>Target Closing Date:</strong> {{closing_date}}</p>
<p><strong>Title:</strong> Seller shall convey marketable title by general warranty deed.</p>

<h2>4. CONDITIONS PRECEDENT</h2>
<p>Buyer's obligation to close is subject to:</p>
<ol>
<li>Satisfactory completion of due diligence</li>
<li>Buyer obtaining financing on acceptable terms (if applicable)</li>
<li>No material adverse change in the Property's condition</li>
<li>Title insurance commitment acceptable to Buyer</li>
</ol>

<h2>5. CONFIDENTIALITY</h2>
<p>Both parties agree to maintain the confidentiality of this LOI and the terms discussed herein.</p>

<h2>6. EXCLUSIVITY</h2>
<p>Upon execution of this LOI, Seller agrees to negotiate exclusively with Buyer for a period of <strong>{{exclusivity_days}} days</strong>.</p>

<h2>7. NON-BINDING NATURE</h2>
<p>This LOI is non-binding and is intended only as a framework for negotiation. No binding obligation shall arise until the parties execute a formal Purchase Agreement.</p>

<h2>8. EXPIRATION</h2>
<p>This LOI shall expire if not accepted by <strong>{{expiration_date}}</strong>.</p>

<p>We believe this transaction presents a mutually beneficial opportunity and look forward to working with you toward a successful closing.</p>

<p>Sincerely,</p>
<br/><br/>
<p>_________________________________<br/>
<strong>{{buyer_name}}</strong><br/>
{{buyer_title}}<br/>
{{buyer_entity}}</p>

<hr/>

<h2>ACCEPTANCE</h2>
<p>The undersigned Seller accepts this Letter of Intent and agrees to negotiate in good faith toward a definitive Purchase Agreement.</p>

<br/><br/>
<p>_________________________________<br/>
<strong>SELLER:</strong> {{seller_name}}<br/>
<strong>DATE:</strong> _______________</p>

<hr/>
<p><em>Generated by DealForge — The Transaction Operating System</em></p>
`
  }
};

// Required fields for validation
const REQUIRED_FIELDS = {
  purchase_agreement: ["buyer_name", "seller_name", "property_address", "purchase_price", "earnest_money", "inspection_days", "closing_date"],
  assignment_agreement: ["assignor_name", "assignee_name", "property_address", "original_price", "assignment_fee", "closing_date"],
  seller_finance: ["buyer_name", "seller_name", "property_address", "purchase_price", "down_payment", "interest_rate", "earnest_money", "inspection_days"],
  wrap_agreement: ["buyer_name", "seller_name", "property_address", "underlying_balance", "underlying_rate", "sale_price", "wrap_rate", "earnest_money"],
  lease_option: ["buyer_name", "seller_name", "property_address", "monthly_rent", "purchase_price", "option_fee"],
  investor_disclosure: ["investor_name", "sponsor_name", "property_address", "total_capital", "projected_roi"],
  letter_of_intent: ["buyer_name", "seller_name", "property_address", "purchase_price", "earnest_money", "closing_date"]
};

// LOCKED FIELDS - pulled from deal/scenario data, non-editable
const LOCKED_FIELDS = {
  purchase_agreement: ["property_address", "purchase_price"],
  assignment_agreement: ["property_address", "original_price"],
  seller_finance: ["property_address", "purchase_price", "down_payment", "interest_rate", "amortization_years", "balloon_years", "monthly_payment"],
  wrap_agreement: ["property_address", "underlying_balance", "underlying_rate", "underlying_payment", "sale_price", "wrap_rate", "buyer_payment", "monthly_spread"],
  subject_to: ["property_address", "purchase_price", "existing_loan_balance", "existing_interest_rate", "existing_monthly_payment"],
  lease_option: ["property_address", "purchase_price", "monthly_rent"]
};

// EDITABLE FIELDS - user-controlled
const EDITABLE_FIELDS = [
  "buyer_name", "seller_name", "assignor_name", "assignee_name",
  "closing_date", "inspection_days", "inspection_start_date", "inspection_end_date",
  "earnest_money", "earnest_days", "settlement_agent",
  "contingencies", "notes", "special_provisions",
  "notice_days", "option_period_days"
];

// TipTap Editor Toolbar Component
const EditorToolbar = ({ editor, printPreviewMode, onTogglePrintPreview }) => {
  if (!editor) return null;
  
  return (
    <div className="flex items-center justify-between p-2 border-b border-white/[0.08] bg-[#0A0A0A]/80 backdrop-blur-sm">
      <div className="flex items-center gap-1">
        <button
          onClick={() => editor.chain().focus().toggleBold().run()}
          className={`p-2 rounded hover:bg-white/[0.06] text-white/70 ${editor.isActive('bold') ? 'bg-white/[0.08] text-gold' : ''}`}
          type="button"
        >
          <Bold className="w-4 h-4" />
        </button>
        <button
          onClick={() => editor.chain().focus().toggleItalic().run()}
          className={`p-2 rounded hover:bg-white/[0.06] text-white/70 ${editor.isActive('italic') ? 'bg-white/[0.08] text-gold' : ''}`}
          type="button"
        >
          <Italic className="w-4 h-4" />
        </button>
        <button
          onClick={() => editor.chain().focus().toggleUnderline().run()}
          className={`p-2 rounded hover:bg-white/[0.06] text-white/70 ${editor.isActive('dealforgeUnderline') ? 'bg-white/[0.08] text-gold' : ''}`}
          type="button"
        >
          <UnderlineIcon className="w-4 h-4" />
        </button>
        <div className="w-px h-6 bg-white/[0.08] mx-1" />
        <button
          onClick={() => editor.chain().focus().toggleHeading({ level: 1 }).run()}
          className={`p-2 rounded hover:bg-white/[0.06] text-white/70 ${editor.isActive('heading', { level: 1 }) ? 'bg-white/[0.08] text-gold' : ''}`}
          type="button"
        >
          <Heading1 className="w-4 h-4" />
        </button>
        <button
          onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}
          className={`p-2 rounded hover:bg-white/[0.06] text-white/70 ${editor.isActive('heading', { level: 2 }) ? 'bg-white/[0.08] text-gold' : ''}`}
          type="button"
        >
          <Heading2 className="w-4 h-4" />
        </button>
        <div className="w-px h-6 bg-white/[0.08] mx-1" />
        <button
          onClick={() => editor.chain().focus().toggleBulletList().run()}
          className={`p-2 rounded hover:bg-white/[0.06] text-white/70 ${editor.isActive('bulletList') ? 'bg-white/[0.08] text-gold' : ''}`}
          type="button"
        >
          <List className="w-4 h-4" />
        </button>
        <button
          onClick={() => editor.chain().focus().toggleOrderedList().run()}
          className={`p-2 rounded hover:bg-white/[0.06] text-white/70 ${editor.isActive('orderedList') ? 'bg-white/[0.08] text-gold' : ''}`}
          type="button"
        >
          <ListOrdered className="w-4 h-4" />
        </button>
      </div>
      
      {/* Print Preview Toggle */}
      <button
        onClick={onTogglePrintPreview}
        className={`flex items-center gap-2 px-3 py-1.5 rounded transition-all ${
          printPreviewMode 
            ? 'bg-[#D4AF37] text-black' 
            : 'bg-white/[0.08] text-white/70 hover:bg-white/[0.1]'
        }`}
        type="button"
      >
        <Printer className="w-4 h-4" />
        <span className="text-xs font-medium">{printPreviewMode ? 'Exit Preview' : 'Print Preview'}</span>
      </button>
    </div>
  );
};

export default function ContractsPage({ user, subscription, onLogout }) {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const autoOpenId = searchParams.get("open");
  const { mainClass } = useSidebar();
  const [contracts, setContracts] = useState([]);
  const [deals, setDeals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditor, setShowEditor] = useState(false);
  const [selectedContract, setSelectedContract] = useState(null);
  const [hasAcceptedDisclaimer, setHasAcceptedDisclaimer] = useState(false);
  const [showDisclaimer, setShowDisclaimer] = useState(false);
  const [showValidationModal, setShowValidationModal] = useState(false);
  const [validationResults, setValidationResults] = useState(null);
  const [acknowledgedWarnings, setAcknowledgedWarnings] = useState(false);
  const [lockedFields, setLockedFields] = useState({});
  const [isContractStale, setIsContractStale] = useState(false);
  const [activeSection, setActiveSection] = useState(null);
  const [contractVersions, setContractVersions] = useState([]);
  const [pendingExportFn, setPendingExportFn] = useState(null);
  const [printPreviewMode, setPrintPreviewMode] = useState(false);
  
  // Custom Template states
  const [customTemplates, setCustomTemplates] = useState([]);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadFile, setUploadFile] = useState(null);
  
  // AI Generate states
  const [showAIModal, setShowAIModal] = useState(false);
  const [aiGenerating, setAIGenerating] = useState(false);
  const [aiForm, setAIForm] = useState({
    contract_type: "purchase_agreement",
    buyer_name: "",
    seller_name: "",
    property_address: "",
    purchase_price: "",
    assignment_fee: "",
    earnest_money: "",
    closing_date: "",
    inspection_days: "10",
    state: "",
    special_terms: "",
  });
  
  // E-Signature states
  const [showSignatureModal, setShowSignatureModal] = useState(false);
  const [signatureForm, setSignatureForm] = useState({
    signer_name: "",
    signer_email: "",
    signer_role: "buyer"
  });
  const [contractSignatures, setContractSignatures] = useState([]);
  const [sendingSignature, setSendingSignature] = useState(false);
  const [signatureFieldMode, setSignatureFieldMode] = useState(false);
  const [signatureFields, setSignatureFields] = useState([]);
  
  const [createForm, setCreateForm] = useState({
    deal_id: "",
    template_type: "",
    name: "",
    fields: {},
    locked_fields: {}
  });

  const tier = subscription?.tier || user?.subscription_tier || "starter";

  // TipTap Editor - use extended Underline with unique name to prevent duplicate registration warnings
  const extensions = useMemo(() => [
    StarterKit,
    Underline.extend({ name: 'dealforgeUnderline' })
  ], []);
  
  const editor = useEditor({
    extensions,
    content: "",
    editorProps: {
      attributes: {
        class: 'prose prose-invert prose-sm max-w-none p-6 min-h-[500px] focus:outline-none text-white/90 prose-headings:text-white prose-p:text-white/70 prose-strong:text-white prose-li:text-white/70'
      }
    }
  });

  useEffect(() => {
    if (tier === "starter") return;
    fetchContracts();
    fetchDeals();
    fetchCustomTemplates();
    const accepted = localStorage.getItem("dealforge_contract_disclaimer");
    setHasAcceptedDisclaimer(accepted === "true");
  }, [tier]);

  const fetchContracts = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/contracts`, { withCredentials: true });
      setContracts(response.data);
      // Auto-open a contract if navigated from Smart Templates
      if (autoOpenId) {
        const target = response.data.find(c => c.contract_id === autoOpenId);
        if (target) {
          setSelectedContract(target);
          if (editor) editor.commands.setContent(target.content || "");
          setShowEditor(true);
        }
      }
    } catch (error) {
      console.error("Failed to fetch contracts:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchDeals = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/deals`, { withCredentials: true });
      setDeals(response.data);
    } catch (error) {
      console.error("Failed to fetch deals:", error);
    }
  };

  const fetchCustomTemplates = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/contracts/custom-templates`, { withCredentials: true });
      setCustomTemplates(response.data);
    } catch (error) {
      console.error("Failed to fetch custom templates:", error);
    }
  };

  const handleFileUpload = async () => {
    if (!uploadFile) return;
    
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', uploadFile);
      
      const response = await axios.post(
        `${BACKEND_URL}/api/contracts/upload-template`,
        formData,
        { 
          withCredentials: true,
          headers: { 'Content-Type': 'multipart/form-data' }
        }
      );
      
      toast.success("Template uploaded successfully!");
      
      if (response.data.warnings?.length > 0) {
        toast.info(`Note: ${response.data.warnings.length} conversion warnings`);
      }
      
      // Refresh custom templates list
      fetchCustomTemplates();
      
      // Open the uploaded template in the editor
      const uploadedTemplate = {
        contract_id: `draft_${Date.now()}`,
        name: response.data.name,
        template_type: "custom",
        content: response.data.html_content,
        fields: {},
        status: "draft",
        is_custom: true,
        custom_template_id: response.data.template_id
      };
      
      setSelectedContract(uploadedTemplate);
      editor?.commands.setContent(response.data.html_content);
      setShowEditor(true);
      setShowUploadModal(false);
      setUploadFile(null);
      
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to upload template");
    } finally {
      setUploading(false);
    }
  };

  const openCustomTemplate = async (templateId) => {
    try {
      const response = await axios.get(
        `${BACKEND_URL}/api/contracts/custom-templates/${templateId}`,
        { withCredentials: true }
      );
      
      const template = response.data;
      const customContract = {
        contract_id: `draft_${Date.now()}`,
        name: template.name,
        template_type: "custom",
        content: template.html_content,
        fields: {},
        status: "draft",
        is_custom: true,
        custom_template_id: templateId
      };
      
      setSelectedContract(customContract);
      editor?.commands.setContent(template.html_content);
      setShowEditor(true);
      
    } catch (error) {
      toast.error("Failed to load template");
    }
  };

  const deleteCustomTemplate = async (templateId) => {
    if (!confirm("Are you sure you want to delete this template?")) return;
    
    try {
      await axios.delete(
        `${BACKEND_URL}/api/contracts/custom-templates/${templateId}`,
        { withCredentials: true }
      );
      toast.success("Template deleted");
      fetchCustomTemplates();
    } catch (error) {
      toast.error("Failed to delete template");
    }
  };

  const fetchContractVersions = async (contractId) => {
    if (!contractId || contractId.startsWith("draft_")) {
      setContractVersions([]);
      return;
    }
    try {
      const response = await axios.get(
        `${BACKEND_URL}/api/contracts/${contractId}/versions`,
        { withCredentials: true }
      );
      setContractVersions(response.data.versions || []);
    } catch (error) {
      console.error("Failed to fetch versions:", error);
      setContractVersions([]);
    }
  };

  const fetchContractSignatures = async (contractId) => {
    if (!contractId || contractId.startsWith("draft_")) {
      setContractSignatures([]);
      return;
    }
    try {
      const response = await axios.get(
        `${BACKEND_URL}/api/contracts/${contractId}/signatures`,
        { withCredentials: true }
      );
      setContractSignatures(response.data || []);
    } catch (error) {
      console.error("Failed to fetch signatures:", error);
      setContractSignatures([]);
    }
  };

  const sendSignatureRequest = async () => {
    if (!selectedContract?.contract_id || selectedContract.contract_id.startsWith("draft_")) {
      toast.error("Please save the contract first before sending for signature");
      return;
    }
    if (!signatureForm.signer_name || !signatureForm.signer_email) {
      toast.error("Please fill in all required fields");
      return;
    }

    setSendingSignature(true);
    try {
      const response = await axios.post(
        `${BACKEND_URL}/api/contracts/${selectedContract.contract_id}/signature-request`,
        signatureForm,
        { withCredentials: true }
      );
      toast.success(`Signature request sent to ${signatureForm.signer_email}`);
      setShowSignatureModal(false);
      setSignatureForm({ signer_name: "", signer_email: "", signer_role: "buyer" });
      fetchContractSignatures(selectedContract.contract_id);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to send signature request");
    } finally {
      setSendingSignature(false);
    }
  };

  const getSignatureLink = (token) => {
    return `${window.location.origin}/sign/${token}`;
  };

  const copySignatureLink = (token) => {
    navigator.clipboard.writeText(getSignatureLink(token));
    toast.success("Signature link copied to clipboard");
  };

  const checkContractStaleness = async (contract) => {
    if (!contract?.deal_id || contract.contract_id?.startsWith("draft_")) {
      setIsContractStale(false);
      return;
    }
    try {
      const dealRes = await axios.get(
        `${BACKEND_URL}/api/deals/${contract.deal_id}`,
        { withCredentials: true }
      );
      const deal = dealRes.data;
      // Check if key deal data has changed since contract was created
      const snapshot = contract.deal_snapshot || {};
      const hasChanges = 
        snapshot.asking_price !== deal.asking_price ||
        snapshot.owner_name !== deal.owner_name ||
        snapshot.property_address !== deal.property_address;
      setIsContractStale(hasChanges);
    } catch (error) {
      setIsContractStale(false);
    }
  };

  const handleAcceptDisclaimer = () => {
    localStorage.setItem("dealforge_contract_disclaimer", "true");
    setHasAcceptedDisclaimer(true);
    setShowDisclaimer(false);
  };

  const handleTemplateSelect = (templateType) => {
    const template = CONTRACT_TEMPLATES[templateType];
    setCreateForm({
      ...createForm,
      template_type: templateType,
      name: template.name
    });
  };

  const handleDealSelect = (dealId) => {
    const deal = deals.find(d => d.deal_id === dealId);
    if (deal) {
      const scenarios = deal.scenarios || [];
      const creativeScenario = scenarios.find(s => 
        ["subject_to", "seller_finance", "wrap"].includes(s.strategy)
      );
      
      // Calculate inspection dates
      const today = new Date();
      const inspectionStart = today.toISOString().split('T')[0];
      const inspectionEnd = new Date(today.getTime() + 10 * 24 * 60 * 60 * 1000).toISOString().split('T')[0];
      
      // Build locked fields from deal data (non-editable calculator values)
      const newLockedFields = {
        property_address: deal.property_address || "",
        purchase_price: deal.asking_price?.toString() || "",
        seller_name: deal.owner_name || "",
      };
      
      // Add scenario-specific locked fields
      if (creativeScenario) {
        Object.assign(newLockedFields, {
          down_payment: creativeScenario.down_payment?.toString() || "",
          interest_rate: creativeScenario.interest_rate?.toString() || "",
          amortization_years: creativeScenario.amortization_years?.toString() || "",
          balloon_years: creativeScenario.balloon_years?.toString() || "",
          monthly_payment: creativeScenario.monthly_payment?.toString() || "",
          // Subject-To specific
          existing_loan_balance: creativeScenario.existing_loan_balance?.toString() || "",
          existing_interest_rate: creativeScenario.existing_interest_rate?.toString() || "",
          existing_monthly_payment: creativeScenario.existing_monthly_payment?.toString() || "",
          // Wrap specific
          underlying_balance: creativeScenario.existing_loan_balance?.toString() || "",
          underlying_rate: creativeScenario.existing_interest_rate?.toString() || "",
          underlying_payment: creativeScenario.existing_monthly_payment?.toString() || "",
          wrap_amount: creativeScenario.financed_amount?.toString() || "",
          buyer_payment: creativeScenario.buyer_monthly_payment?.toString() || "",
          monthly_spread: creativeScenario.monthly_spread?.toString() || ""
        });
      }
      
      setLockedFields(newLockedFields);
      
      setCreateForm({
        ...createForm,
        deal_id: dealId,
        locked_fields: newLockedFields,
        fields: {
          ...createForm.fields,
          // Editable fields only
          inspection_days: "10",
          inspection_start_date: inspectionStart,
          inspection_end_date: inspectionEnd,
          earnest_money: "",
          earnest_days: "3",
          escrow_agent: "",
          buyer_name: "",
          closing_date: ""
        }
      });
    }
  };

  const updateInspectionDates = (days) => {
    const startDate = createForm.fields.inspection_start_date || new Date().toISOString().split('T')[0];
    const start = new Date(startDate);
    const end = new Date(start.getTime() + parseInt(days || 10) * 24 * 60 * 60 * 1000);
    
    setCreateForm({
      ...createForm,
      fields: {
        ...createForm.fields,
        inspection_days: days,
        inspection_end_date: end.toISOString().split('T')[0]
      }
    });
  };

  const generateContract = () => {
    if (!hasAcceptedDisclaimer) {
      setShowDisclaimer(true);
      return;
    }
    
    try {
      const template = CONTRACT_TEMPLATES[createForm.template_type];
      if (!template) {
        toast.error("Please select a contract template");
        return;
      }
      
      let content = template.template;
      
      // Merge locked fields (from deal) and editable fields
      const allFields = { ...lockedFields, ...createForm.fields };
      
      // Replace all placeholders with field values
      Object.entries(allFields).forEach(([key, value]) => {
        const regex = new RegExp(`\\{\\{${key}\\}\\}`, 'g');
        content = content.replace(regex, value || `[${key.replace(/_/g, ' ').toUpperCase()}]`);
      });
      
      // Replace remaining placeholders with highlighted markers
      content = content.replace(/\{\{(\w+)\}\}/g, '<mark style="background-color: #fef3c7; padding: 2px 4px;">[$1]</mark>');
      
      // Set editor content
      if (editor) {
        editor.commands.setContent(content);
      }
      
      setSelectedContract({
        contract_id: `draft_${Date.now()}`,
        name: createForm.name || `${template.name} Draft`,
        template_type: createForm.template_type,
        deal_id: createForm.deal_id,
        fields: createForm.fields,
        locked_fields: lockedFields,
        content: content,
        created_at: new Date().toISOString(),
        versions: []
      });
      setIsContractStale(false);
      setShowCreateModal(false);
      setShowEditor(true);
    } catch (error) {
      console.error("Error generating contract:", error);
      toast.error("Failed to generate contract. Please try again.");
    }
  };

  const generateAIContract = async () => {
    if (!hasAcceptedDisclaimer) { setShowDisclaimer(true); return; }
    if (!aiForm.buyer_name || !aiForm.seller_name || !aiForm.property_address) {
      toast.error("Fill in buyer name, seller name, and property address");
      return;
    }
    setAIGenerating(true);
    try {
      const payload = {
        contract_type: aiForm.contract_type,
        buyer_name: aiForm.buyer_name,
        seller_name: aiForm.seller_name,
        property_address: aiForm.property_address,
        purchase_price: aiForm.purchase_price ? parseFloat(aiForm.purchase_price) : null,
        assignment_fee: aiForm.assignment_fee ? parseFloat(aiForm.assignment_fee) : null,
        earnest_money: aiForm.earnest_money ? parseFloat(aiForm.earnest_money) : null,
        closing_date: aiForm.closing_date || null,
        inspection_days: parseInt(aiForm.inspection_days) || 10,
        state: aiForm.state || null,
        special_terms: aiForm.special_terms || null,
      };
      const res = await axios.post(`${BACKEND_URL}/api/contracts/generate-ai`, payload, { withCredentials: true });
      const html = res.data.html_content;
      if (editor) editor.commands.setContent(html);
      const contractNames = {
        purchase_agreement: "Purchase Agreement",
        assignment: "Assignment Agreement",
        lease_option: "Lease Option",
        letter_of_intent: "Letter of Intent",
      };
      setSelectedContract({
        contract_id: `draft_ai_${Date.now()}`,
        name: `${contractNames[aiForm.contract_type] || "Contract"} — AI Draft`,
        template_type: aiForm.contract_type,
        deal_id: null,
        fields: {},
        locked_fields: {},
        content: html,
        created_at: new Date().toISOString(),
        versions: [],
      });
      setIsContractStale(false);
      setShowAIModal(false);
      setShowEditor(true);
      toast.success("AI contract generated — review and customize before signing");
    } catch (err) {
      toast.error(err.response?.data?.detail || "AI generation failed. Try again.");
    } finally {
      setAIGenerating(false);
    }
  };

  const validateContract = async () => {    if (!selectedContract || selectedContract.contract_id?.startsWith("draft_")) {
      // For new drafts, do client-side validation only
      const warnings = [];
      const templateType = selectedContract?.template_type;
      const allFields = { ...lockedFields, ...createForm.fields };
      
      // Assignment fee check
      if (templateType === "assignment_agreement") {
        const fee = parseFloat(allFields.assignment_fee || 0);
        const price = parseFloat(allFields.original_price || 0);
        if (price > 0 && fee > price * 0.15) {
          warnings.push({
            type: "high_assignment_fee",
            message: `Assignment fee ($${fee.toLocaleString()}) is more than 15% of purchase price.`,
            severity: "medium"
          });
        }
      }
      
      // Balloon check
      const balloonYears = parseFloat(allFields.balloon_years || 0);
      if (balloonYears > 0 && balloonYears <= 3) {
        warnings.push({
          type: "short_balloon",
          message: `Balloon payment due in ${balloonYears} years. Ensure buyer can refinance.`,
          severity: "high"
        });
      }
      
      // Wrap spread check
      if (templateType === "wrap_agreement") {
        const spread = parseFloat(allFields.monthly_spread || 0);
        if (spread > 0 && spread < 100) {
          warnings.push({
            type: "low_wrap_spread",
            message: `Monthly spread ($${spread.toFixed(0)}) is below $100. Minimal profit margin.`,
            severity: "medium"
          });
        }
      }
      
      setValidationResults({ warnings, errors: [], is_valid: true, can_export: true });
      if (warnings.length > 0) {
        setShowValidationModal(true);
      }
      return warnings.length === 0;
    }
    
    // For saved contracts, call backend validation
    try {
      const response = await axios.post(
        `${BACKEND_URL}/api/contracts/${selectedContract.contract_id}/validate`,
        {},
        { withCredentials: true }
      );
      setValidationResults(response.data);
      if (response.data.warnings?.length > 0 || response.data.errors?.length > 0) {
        setShowValidationModal(true);
      }
      return response.data.can_export;
    } catch (error) {
      toast.error("Failed to validate contract");
      return true; // Allow export even if validation fails
    }
  };

  const refreshFromDeal = async () => {
    if (!selectedContract?.contract_id || selectedContract.contract_id.startsWith("draft_")) {
      toast.error("Save the contract first to refresh from deal");
      return;
    }
    
    try {
      const response = await axios.post(
        `${BACKEND_URL}/api/contracts/${selectedContract.contract_id}/refresh`,
        {},
        { withCredentials: true }
      );
      toast.success("Contract refreshed from deal data");
      setLockedFields(response.data.locked_fields);
      setIsContractStale(false);
      // Re-fetch the contract
      const contractRes = await axios.get(
        `${BACKEND_URL}/api/contracts/${selectedContract.contract_id}`,
        { withCredentials: true }
      );
      setSelectedContract(contractRes.data);
    } catch (error) {
      toast.error("Failed to refresh contract");
    }
  };

  const saveContract = async () => {
    if (!editor) return;
    
    try {
      const content = editor.getHTML();
      const contractData = {
        ...selectedContract,
        content: content,
        locked_fields: lockedFields,
        updated_at: new Date().toISOString()
      };
      
      if (selectedContract.contract_id?.startsWith("draft_")) {
        const response = await axios.post(`${BACKEND_URL}/api/contracts`, contractData, { withCredentials: true });
        setSelectedContract(response.data);
        toast.success("Contract saved");
      } else {
        await axios.put(`${BACKEND_URL}/api/contracts/${selectedContract.contract_id}`, contractData, { withCredentials: true });
        toast.success("Contract updated");
      }
      
      fetchContracts();
    } catch (error) {
      toast.error("Failed to save contract");
    }
  };

  const duplicateContract = async () => {
    if (!selectedContract) return;
    
    const newContract = {
      ...selectedContract,
      contract_id: `draft_${Date.now()}`,
      name: `${selectedContract.name} (Copy)`,
      created_at: new Date().toISOString(),
      versions: []
    };
    
    setSelectedContract(newContract);
    toast.success("Contract duplicated. Save to create a new copy.");
  };

  const handleExportWithValidation = async (exportFn) => {
    setPendingExportFn(() => exportFn);
    setAcknowledgedWarnings(false);
    await validateContract();
    
    // If there are warnings, the modal will show and user must acknowledge
    // The actual export happens in proceedWithExport after acknowledgment
    if (!validationResults?.warnings?.length) {
      proceedWithExport(exportFn);
    }
  };

  const proceedWithExport = async (exportFn) => {
    // Log export activity
    if (selectedContract?.contract_id && !selectedContract.contract_id.startsWith("draft_")) {
      try {
        await axios.post(
          `${BACKEND_URL}/api/contracts/${selectedContract.contract_id}/activity?action=${exportFn === exportToPDF ? 'exported_pdf' : 'exported_docx'}`,
          {},
          { withCredentials: true }
        );
      } catch (e) { /* ignore */ }
    }
    
    exportFn();
  };

  const exportToPDF = () => {
    if (!editor) return;
    
    const doc = new jsPDF();
    const text = editor.getText();
    const lines = doc.splitTextToSize(text, 180);
    
    doc.setFontSize(10);
    let y = 20;
    lines.forEach((line) => {
      if (y > 280) {
        doc.addPage();
        y = 20;
      }
      doc.text(line, 15, y);
      y += 5;
    });
    
    doc.save(`${selectedContract.name.replace(/\s+/g, '_')}.pdf`);
    toast.success("PDF exported");
  };

  const exportToDocx = async () => {
    if (!editor) return;
    
    const text = editor.getText();
    const doc = new Document({
      sections: [{
        properties: {},
        children: text.split("\n").map(line => 
          new Paragraph({ children: [new TextRun(line)] })
        )
      }]
    });
    
    const blob = await Packer.toBlob(doc);
    saveAs(blob, `${selectedContract.name.replace(/\s+/g, '_')}.docx`);
    toast.success("DOCX exported");
  };

  const getFieldValidation = () => {
    const required = REQUIRED_FIELDS[createForm.template_type] || [];
    return required.map(field => ({
      field,
      label: field.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()),
      filled: !!createForm.fields[field]
    }));
  };

  // Feature gate
  if (tier === "starter") {
    return (
      <div className="min-h-screen bg-black text-white">
        <Sidebar user={user} subscription={subscription} onLogout={onLogout} />
        <main className={mainClass}>
          <div className="max-w-2xl mx-auto text-center py-16 px-6">
            <div className="w-20 h-20 bg-[#D4AF37]/[0.08] border border-[#D4AF37]/30 flex items-center justify-center mx-auto mb-6 rounded-lg">
              <FileSignature className="w-10 h-10 text-[#D4AF37]" />
            </div>
            <h1 className="text-2xl font-semibold text-white mb-4">Contract <span className="text-[#D4AF37]">Drafting</span></h1>
            <p className="text-white/50 mb-8">Generate professional contract drafts from your deal data.</p>
            <Link to="/upgrade">
              <button className="px-8 py-3 bg-gradient-to-br from-[#D4AF37] to-[#AA8C2C] text-black font-semibold rounded-sm hover:opacity-90 transition-opacity">
                Upgrade to Pro to Access
              </button>
            </Link>
          </div>
        </main>
      </div>
    );
  }

  // Disclaimer Modal
  if (showDisclaimer && !hasAcceptedDisclaimer) {
    return (
      <div className="min-h-screen bg-black text-white">
        <Sidebar user={user} subscription={subscription} onLogout={onLogout} />
        <main className={mainClass}>
          <div className="max-w-2xl mx-auto p-6">
            <div className="bg-[#0A0A0A]/80 backdrop-blur-sm border border-amber-500/30 rounded-lg p-8">
              <div className="flex items-center gap-4 mb-6">
                <AlertTriangle className="w-10 h-10 text-amber-500" />
                <h2 className="text-xl font-semibold text-white">Important Disclaimer</h2>
              </div>
              
              <div className="bg-black/80 border border-white/[0.06] rounded-lg p-6 mb-6">
                <p className="text-white/70 mb-4">The contract drafting feature generates documents from user-provided data and pre-built templates.</p>
                <p className="text-[#FF9F0A] drop-shadow-[0_0_8px_rgba(255,159,10,0.5)] font-semibold mb-4">THIS IS NOT LEGAL ADVICE.</p>
                <ul className="text-white/50 space-y-2 text-sm">
                  <li>• These documents are drafts only</li>
                  <li>• Laws vary by state and jurisdiction</li>
                  <li>• Always have a licensed attorney review contracts</li>
                </ul>
              </div>
              
              <div className="flex items-start gap-3 mb-6">
                <Checkbox 
                  id="disclaimer" 
                  checked={hasAcceptedDisclaimer}
                  onCheckedChange={(checked) => setHasAcceptedDisclaimer(checked)}
                />
                <label htmlFor="disclaimer" className="text-white/70 text-sm cursor-pointer">
                  I understand these are draft documents and will have an attorney review before use.
                </label>
              </div>
              
              <button 
                onClick={handleAcceptDisclaimer}
                disabled={!hasAcceptedDisclaimer}
                className="w-full py-3 bg-gradient-to-br from-[#D4AF37] to-[#AA8C2C] text-black font-semibold rounded-sm hover:opacity-90 transition-opacity disabled:opacity-50 disabled:cursor-not-allowed"
              >
                I Understand - Continue
              </button>
            </div>
          </div>
        </main>
      </div>
    );
  }

  // Editor View
  if (showEditor && selectedContract) {
    const validation = getFieldValidation();
    const templateSections = CONTRACT_TEMPLATES[selectedContract.template_type]?.sections || [];
    
    return (
      <div className="min-h-screen bg-black text-white">
        <Sidebar user={user} subscription={subscription} onLogout={onLogout} />
        
        <main className={`${mainClass} flex-1 flex flex-col min-h-screen`}>
          {/* Header */}
          <div className="bg-[#0A0A0A]/80 backdrop-blur-sm border-b border-white/[0.06] px-6 py-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <button 
                  onClick={() => { setShowEditor(false); setSelectedContract(null); setContractVersions([]); setContractSignatures([]); }} 
                  className="text-white/50 hover:text-white transition-all duration-300"
                >
                  ← Back
                </button>
                <div>
                  <h1 className="text-white font-semibold flex items-center gap-2">
                    {selectedContract.name}
                    {selectedContract.locked_fields && Object.keys(selectedContract.locked_fields).length > 0 && (
                      <span className="px-2 py-0.5 bg-[#D4AF37]/[0.08] text-[#D4AF37] text-[10px] font-medium border border-[#D4AF37]/30 rounded">
                        <Lock className="w-3 h-3 inline mr-1" />Deal-Linked
                      </span>
                    )}
                  </h1>
                  <p className="text-white/50 text-sm">{CONTRACT_TEMPLATES[selectedContract.template_type]?.name}</p>
                </div>
              </div>
              
              <div className="flex items-center gap-3">
                {!selectedContract.contract_id?.startsWith("draft_") && (
                  <button 
                    onClick={() => { refreshFromDeal(); }}
                    className="flex items-center gap-2 px-3 py-1.5 border border-white/[0.06] text-white/70 hover:text-white hover:border-white/[0.15] rounded-sm text-sm transition-all duration-300"
                    title="Refresh data from deal"
                  >
                    <RefreshCw className="w-4 h-4" />Refresh
                  </button>
                )}
                <button onClick={duplicateContract} className="flex items-center gap-2 px-3 py-1.5 border border-white/[0.06] text-white/70 hover:text-white hover:border-white/[0.15] rounded-sm text-sm transition-all duration-300">
                  <Copy className="w-4 h-4" />Duplicate
                </button>
                <Button variant="outline" onClick={() => handleExportWithValidation(exportToDocx)} className="border-white/[0.1] text-white/70 hover:bg-white/[0.06] rounded-none">
                  <Download className="w-4 h-4 mr-2" />DOCX
                </Button>
                <Button variant="outline" onClick={() => handleExportWithValidation(exportToPDF)} className="border-white/[0.1] text-white/70 hover:bg-white/[0.06] rounded-none">
                  <Download className="w-4 h-4 mr-2" />PDF
                </Button>
                {!selectedContract.contract_id?.startsWith("draft_") && (
                  <Button 
                    variant="outline" 
                    onClick={() => setShowSignatureModal(true)}
                    className="border-emerald-500/50 text-[#00FF88] drop-shadow-[0_0_10px_rgba(0,255,136,0.5)] hover:bg-emerald-500/10 rounded-none"
                  >
                    <Send className="w-4 h-4 mr-2" />Send for Signature
                  </Button>
                )}
                <Button 
                  variant="outline" 
                  onClick={() => setSignatureFieldMode(!signatureFieldMode)}
                  className={`rounded-none ${
                    signatureFieldMode 
                      ? 'bg-blue-500/20 border-blue-500 text-[#0A84FF] drop-shadow-[0_0_8px_rgba(10,132,255,0.4)]' 
                      : 'border-white/[0.1] text-white/70 hover:bg-white/[0.06]'
                  }`}
                >
                  <PenLine className="w-4 h-4 mr-2" />
                  {signatureFieldMode ? 'Exit Field Mode' : 'Place Fields'}
                </Button>
                <Button onClick={saveContract} className="gold-gradient-btn rounded-none font-bold">
                  <Save className="w-4 h-4 mr-2" />Save Draft
                </Button>
              </div>
            </div>
            
            {/* Staleness Warning */}
            {isContractStale && (
              <div className="mt-3 p-3 bg-amber-500/10 border border-amber-500/30 flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <AlertTriangle className="w-5 h-5 text-amber-500" />
                  <span className="text-[#FF9F0A] drop-shadow-[0_0_8px_rgba(255,159,10,0.5)] text-sm">
                    Deal data has changed since this contract was created. Some locked fields may be outdated.
                  </span>
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={refreshFromDeal}
                  className="border-amber-500/50 text-[#FF9F0A] drop-shadow-[0_0_8px_rgba(255,159,10,0.5)] hover:bg-amber-500/10 rounded-none"
                >
                  <RefreshCw className="w-4 h-4 mr-1" />Update Now
                </Button>
              </div>
            )}
          </div>

          <div className="flex-1 flex overflow-hidden">
            {/* Left Sidebar - Sections & Checklist */}
            <div className="w-64 bg-[#0A0A0A]/80 backdrop-blur-sm border-r border-white/[0.08] overflow-y-auto">
              {/* Section Navigation */}
              <div className="p-4 border-b border-white/[0.08]">
                <h3 className="text-xs text-white/70 uppercase font-bold mb-3">Sections</h3>
                <div className="space-y-1">
                  {templateSections.map((section) => (
                    <button
                      key={section}
                      onClick={() => setActiveSection(section)}
                      className={`w-full text-left px-3 py-2 text-sm flex items-center justify-between transition-all duration-300 ${
                        activeSection === section ? 'bg-gold/20 text-gold' : 'text-white/70 hover:bg-white/[0.06] hover:text-white'
                      }`}
                    >
                      <span className="capitalize">{section.replace(/_/g, ' ')}</span>
                      <ChevronRight className="w-4 h-4" />
                    </button>
                  ))}
                </div>
              </div>

              {/* Field Checklist */}
              <div className="p-4 border-b border-white/[0.08]">
                <h3 className="text-xs text-white/70 uppercase font-bold mb-3">Field Checklist</h3>
                <div className="space-y-2">
                  {validation.map((item) => (
                    <div key={item.field} className="flex items-center gap-2 text-sm">
                      {item.filled ? <CheckCircle className="w-4 h-4 text-emerald-500" /> : <XCircle className="w-4 h-4 text-red-500" />}
                      <span className={item.filled ? "text-white/70" : "text-[#FF3B30]"}>{item.label}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Locked Fields Info */}
              {selectedContract.locked_fields && Object.keys(selectedContract.locked_fields).length > 0 && (
                <div className="p-4 border-b border-white/[0.08]">
                  <h3 className="text-xs text-white/70 uppercase font-bold mb-3 flex items-center gap-2">
                    <Lock className="w-3 h-3" />Locked Fields
                  </h3>
                  <p className="text-white/70 text-xs mb-2">From deal calculator:</p>
                  <div className="space-y-1">
                    {Object.entries(selectedContract.locked_fields || lockedFields).slice(0, 5).map(([key, value]) => (
                      <div key={key} className="text-xs text-white/70 flex justify-between">
                        <span className="capitalize">{key.replace(/_/g, ' ')}</span>
                        <span className="text-white font-mono">{value || '-'}</span>
                      </div>
                    ))}
                    {Object.keys(selectedContract.locked_fields || lockedFields).length > 5 && (
                      <p className="text-xs text-white/70 mt-1">+{Object.keys(selectedContract.locked_fields || lockedFields).length - 5} more...</p>
                    )}
                  </div>
                </div>
              )}

              {/* Version History */}
              <div className="p-4 border-b border-white/[0.08]">
                <h3 className="text-xs text-white/70 uppercase font-bold mb-3 flex items-center gap-2">
                  <History className="w-3 h-3" />Version History
                </h3>
                {contractVersions.length === 0 ? (
                  <p className="text-white/70 text-xs">No previous versions saved yet.</p>
                ) : (
                  <div className="space-y-2">
                    {contractVersions.slice(0, 5).map((version, idx) => (
                      <div key={idx} className="p-2 bg-black border border-white/[0.08] text-xs">
                        <div className="flex items-center justify-between">
                          <span className="text-white/70">v{contractVersions.length - idx}</span>
                          <span className="text-white/70">{new Date(version.saved_at).toLocaleDateString()}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* E-Signatures */}
              {!selectedContract.contract_id?.startsWith("draft_") && (
                <div className="p-4">
                  <h3 className="text-xs text-white/70 uppercase font-bold mb-3 flex items-center gap-2">
                    <FileSignature className="w-3 h-3" />Signatures
                  </h3>
                  {contractSignatures.length === 0 ? (
                    <p className="text-white/70 text-xs">No signature requests yet.</p>
                  ) : (
                    <div className="space-y-2">
                      {contractSignatures.map((sig) => (
                        <div key={sig.signature_id} className="p-2 bg-black border border-white/[0.08]">
                          <div className="flex items-center justify-between mb-1">
                            <span className="text-white/70 text-xs font-medium">{sig.signer_name}</span>
                            <span className={`text-xs px-1.5 py-0.5 ${
                              sig.status === 'signed' ? 'bg-emerald-500/20 text-[#00FF88] drop-shadow-[0_0_10px_rgba(0,255,136,0.5)]' :
                              sig.status === 'declined' ? 'bg-red-500/20 text-[#FF3B30]' :
                              'bg-amber-500/20 text-[#FF9F0A] drop-shadow-[0_0_8px_rgba(255,159,10,0.5)]'
                            }`}>
                              {sig.status}
                            </span>
                          </div>
                          <p className="text-white/70 text-xs capitalize">{sig.signer_role}</p>
                          {sig.status === 'pending' && (
                            <button
                              onClick={() => copySignatureLink(sig.token)}
                              className="mt-1 text-xs text-gold hover:underline flex items-center gap-1"
                            >
                              <ExternalLink className="w-3 h-3" />Copy Link
                            </button>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => setShowSignatureModal(true)}
                    className="w-full mt-3 border-emerald-500/30 text-[#00FF88] drop-shadow-[0_0_10px_rgba(0,255,136,0.5)] hover:bg-emerald-500/10 rounded-none text-xs"
                  >
                    <Send className="w-3 h-3 mr-1" />Request Signature
                  </Button>
                </div>
              )}
            </div>

            {/* Editor */}
            <div className={`flex-1 overflow-y-auto flex flex-col ${
              printPreviewMode 
                ? 'bg-white/[0.1] p-8' 
                : 'bg-black'
            }`}>
              <EditorToolbar 
                editor={editor} 
                printPreviewMode={printPreviewMode}
                onTogglePrintPreview={() => setPrintPreviewMode(!printPreviewMode)}
              />
              <div className={`flex-1 overflow-y-auto ${
                printPreviewMode 
                  ? 'flex justify-center' 
                  : ''
              }`}>
                {signatureFieldMode ? (
                  /* Signature Field Placement Mode */
                  <SignatureFieldPlacer
                    contractContent={editor?.getHTML() || selectedContract.content || ""}
                    initialFields={signatureFields}
                    onFieldsChange={setSignatureFields}
                    readOnly={false}
                  />
                ) : (
                  /* Normal Editor Mode */
                  <div className={printPreviewMode 
                    ? 'bg-white shadow-2xl w-[8.5in] min-h-[11in] p-[1in]' 
                    : ''
                  }>
                    <EditorContent 
                      editor={editor} 
                      className={printPreviewMode 
                        ? 'prose prose-sm max-w-none min-h-full text-black prose-headings:text-black prose-p:text-gray-800' 
                        : 'min-h-full'
                      } 
                    />
                  </div>
                )}
              </div>
            </div>
          </div>
        </main>

        {/* Validation Modal */}
        <Dialog open={showValidationModal} onOpenChange={setShowValidationModal}>
          <DialogContent className="bg-[#0A0A0A]/80 backdrop-blur-sm border-white/[0.08] text-white max-w-lg">
            <DialogHeader>
              <DialogTitle className="font-heading text-xl uppercase flex items-center gap-2">
                <AlertTriangle className="w-5 h-5 text-amber-500" />
                Pre-Export Warnings
              </DialogTitle>
            </DialogHeader>
            
            <div className="space-y-4 mt-4">
              <p className="text-white/70 text-sm">
                The following issues were found. Please review before exporting:
              </p>
              
              {validationResults?.warnings?.map((warning, idx) => (
                <div
                  key={idx}
                  className={`p-3 border ${
                    warning.severity === 'high' 
                      ? 'bg-red-500/10 border-red-500/30' 
                      : 'bg-amber-500/10 border-amber-500/30'
                  }`}
                >
                  <div className="flex items-start gap-2">
                    <AlertTriangle className={`w-4 h-4 mt-0.5 ${
                      warning.severity === 'high' ? 'text-red-500' : 'text-amber-500'
                    }`} />
                    <div>
                      <p className={warning.severity === 'high' ? 'text-[#FF3B30]' : 'text-[#FF9F0A] drop-shadow-[0_0_8px_rgba(255,159,10,0.5)]'}>
                        {warning.message}
                      </p>
                      <p className="text-white/70 text-xs mt-1 uppercase">{warning.type}</p>
                    </div>
                  </div>
                </div>
              ))}
              
              <div className="flex items-start gap-3 p-3 bg-black border border-white/[0.08]">
                <Checkbox 
                  id="acknowledge" 
                  checked={acknowledgedWarnings}
                  onCheckedChange={(checked) => setAcknowledgedWarnings(checked)}
                />
                <label htmlFor="acknowledge" className="text-white/70 text-sm cursor-pointer">
                  I have reviewed these warnings and wish to proceed with export.
                </label>
              </div>
              
              <div className="flex justify-end gap-3 pt-4 border-t border-white/[0.08]">
                <Button 
                  variant="outline" 
                  onClick={() => setShowValidationModal(false)} 
                  className="border-white/[0.1] text-white/70 hover:bg-white/[0.06] rounded-none"
                >
                  Cancel
                </Button>
                <Button 
                  onClick={() => {
                    if (acknowledgedWarnings && pendingExportFn) {
                      proceedWithExport(pendingExportFn);
                      setShowValidationModal(false);
                    }
                  }}
                  disabled={!acknowledgedWarnings}
                  className="gold-gradient-btn rounded-none font-bold"
                >
                  <Download className="w-4 h-4 mr-2" />Proceed with Export
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    );
  }

  // Main List View
  return (
    <div className="min-h-screen bg-black text-white">
      <Sidebar user={user} subscription={subscription} onLogout={onLogout} />
      
      <main className={mainClass} data-testid="contracts-page">
        {/* System Overview Strip */}
        <header className="border-b border-white/[0.06] bg-[#0A0A0A]/80 backdrop-blur-sm">
          <div className="px-6 py-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-6">
                <div>
                  <h1 className="text-lg font-semibold tracking-tight">
                    Contract <span className="text-[#D4AF37]">Drafting</span>
                  </h1>
                  <p className="text-[9px] text-white/50 uppercase tracking-[0.15em]">Generate, edit, and execute legally compliant real estate agreements.</p>
                </div>
                
                <div className="flex items-center gap-2 px-3 py-1.5 bg-emerald-500/[0.08] border border-emerald-500/20 rounded">
                  <div className="relative w-2 h-2">
                    <div className="absolute inset-0 bg-emerald-400 rounded-full" />
                    <div className="absolute inset-0 bg-emerald-400 rounded-full animate-ping opacity-75" />
                  </div>
                  <span className="text-[10px] font-semibold text-[#00FF88] drop-shadow-[0_0_10px_rgba(0,255,136,0.5)] uppercase tracking-wider">Live</span>
                </div>
              </div>

              <div className="flex items-center gap-8">
                <div>
                  <div className="text-[9px] font-medium text-white/50 uppercase tracking-[0.12em] mb-1">Templates</div>
                  <span className="text-xl font-light text-white">{Object.keys(CONTRACT_TEMPLATES).length + customTemplates.length}</span>
                </div>
                <div className="w-px h-10 bg-white/[0.04]" />
                <div>
                  <div className="text-[9px] font-medium text-white/50 uppercase tracking-[0.12em] mb-1">Drafts</div>
                  <span className="text-xl font-light text-[#D4AF37]">{contracts.length}</span>
                </div>
                <div className="w-px h-10 bg-white/[0.04]" />
                <button 
                  onClick={() => setShowCreateModal(true)} 
                  className="flex items-center gap-2 px-4 py-2 bg-gradient-to-br from-[#D4AF37] to-[#AA8C2C] text-black font-semibold text-sm rounded-sm hover:opacity-90 transition-opacity"
                  data-testid="create-contract-btn"
                >
                  <Plus className="w-4 h-4" />
                  New Contract
                </button>
                <button
                  onClick={() => setShowAIModal(true)}
                  className="flex items-center gap-2 px-4 py-2 bg-purple-500/10 border border-purple-500/30 text-[#BF5AF2] font-semibold text-sm rounded-sm hover:bg-purple-500/20 transition-all"
                  data-testid="ai-generate-contract-btn"
                >
                  <Sparkles className="w-4 h-4" />
                  AI Generate
                </button>
              </div>
            </div>
          </div>
        </header>

        <div className="p-6">
          {/* Template Cards */}
          <div className="mb-8">
            <h2 className="text-xs font-semibold text-white/70 uppercase tracking-wider mb-4">Standard Templates</h2>
            <div className="grid md:grid-cols-3 lg:grid-cols-5 gap-3">
              {Object.entries(CONTRACT_TEMPLATES).map(([key, template]) => (
                <button
                  key={key}
                  onClick={() => { handleTemplateSelect(key); setShowCreateModal(true); }}
                  className="bg-[#0A0A0A]/80 backdrop-blur-sm border border-white/[0.06] rounded-lg p-4 text-left hover:border-[#D4AF37]/30 transition-all duration-300 group"
                >
                  <FileText className="w-6 h-6 text-[#D4AF37] mb-3 group-hover:scale-110 transition-transform" />
                  <h3 className="text-white font-medium text-sm mb-1">{template.name}</h3>
                  <p className="text-white/40 text-[11px]">{template.description}</p>
                </button>
              ))}
              
              {/* Upload Custom Template Button */}
              <button
                onClick={() => setShowUploadModal(true)}
                className="bg-black/80 border border-dashed border-white/[0.1] rounded-lg p-4 text-left hover:border-[#D4AF37]/30 transition-all duration-300 group"
                data-testid="upload-template-btn"
              >
                <Upload className="w-6 h-6 text-white/40 mb-3 group-hover:text-[#D4AF37] group-hover:scale-110 transition-all" />
                <h3 className="text-white/50 group-hover:text-white font-medium text-sm mb-1">Upload Template</h3>
                <p className="text-white/30 text-[11px]">Import your .docx files</p>
              </button>
            </div>
          </div>

          {/* Custom Templates Section */}
          {customTemplates.length > 0 && (
            <div className="mb-8">
              <h2 className="text-xs font-semibold text-white/70 uppercase tracking-wider mb-4">Your Custom Templates</h2>
              <div className="grid md:grid-cols-3 lg:grid-cols-4 gap-3">
                {customTemplates.map((template) => (
                  <div
                    key={template.template_id}
                    className="bg-[#0A0A0A]/80 backdrop-blur-sm border border-white/[0.06] rounded-lg p-4 hover:border-[#D4AF37]/30 transition-all duration-300 group relative"
                  >
                    <button
                      onClick={() => openCustomTemplate(template.template_id)}
                      className="w-full text-left"
                    >
                      <FileUp className="w-6 h-6 text-[#00FF88] drop-shadow-[0_0_10px_rgba(0,255,136,0.5)] mb-3 group-hover:scale-110 transition-transform" />
                      <h3 className="text-white font-medium text-sm mb-1 truncate">{template.name}</h3>
                      <p className="text-white/40 text-[11px]">{template.original_filename}</p>
                    </button>
                    <button
                      onClick={(e) => { e.stopPropagation(); deleteCustomTemplate(template.template_id); }}
                      className="absolute top-3 right-3 p-1.5 bg-white/[0.02] hover:bg-red-500/20 text-white/40 hover:text-[#FF3B30] rounded transition-all duration-300 opacity-0 group-hover:opacity-100"
                      title="Delete template"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Saved Contracts */}
          <div className="bg-[#0A0A0A]/80 backdrop-blur-sm border border-white/[0.06] rounded-lg">
            <div className="flex items-center justify-between px-5 py-4 border-b border-white/[0.06]">
              <div>
                <h2 className="text-sm font-semibold text-white">Saved Drafts</h2>
                <p className="text-[10px] text-white/50 mt-0.5">Your contract documents</p>
              </div>
            </div>
            
            {loading ? (
              <div className="flex items-center justify-center py-16">
                <div className="w-8 h-8 border-2 border-[#D4AF37] border-t-transparent rounded-full animate-spin" />
              </div>
            ) : contracts.length === 0 ? (
              <div className="text-center py-16 px-6">
                <FileSignature className="w-16 h-16 text-white/30 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-white mb-2">No Contracts Yet</h3>
                <p className="text-sm text-white/50 mb-6">Create your first contract draft from a transaction.</p>
                <button 
                  onClick={() => setShowCreateModal(true)}
                  className="px-6 py-2.5 bg-gradient-to-br from-[#D4AF37] to-[#AA8C2C] text-black font-semibold rounded-sm hover:opacity-90 transition-opacity"
                >
                  <Plus className="w-4 h-4 inline mr-2" />
                  Create Contract
                </button>
              </div>
            ) : (
              <div className="divide-y divide-white/[0.02]">
                {contracts.map((contract) => (
                  <div 
                    key={contract.contract_id} 
                    className="px-5 py-4 flex items-center justify-between hover:bg-white/[0.02] transition-all duration-300"
                  >
                    <div className="flex items-center gap-4">
                      <div className="w-10 h-10 bg-[#D4AF37]/[0.08] border border-[#D4AF37]/20 rounded-lg flex items-center justify-center">
                        <FileText className="w-5 h-5 text-[#D4AF37]" />
                      </div>
                      <div>
                        <h3 className="text-white font-medium">{contract.name}</h3>
                        <p className="text-white/50 text-[11px]">
                          {CONTRACT_TEMPLATES[contract.template_type]?.name || 'Custom'} • Last edited {new Date(contract.updated_at || contract.created_at).toLocaleDateString()}
                        </p>
                      </div>
                    </div>
                    <button
                      onClick={() => {
                        setSelectedContract(contract);
                        if (editor) editor.commands.setContent(contract.content);
                        setShowEditor(true);
                        fetchContractVersions(contract.contract_id);
                        fetchContractSignatures(contract.contract_id);
                      }}
                      className="flex items-center gap-2 px-3 py-1.5 text-white/50 hover:text-[#D4AF37] text-sm transition-all duration-300"
                    >
                      <Edit3 className="w-4 h-4" />
                      Edit
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Create Modal */}
        <Dialog open={showCreateModal} onOpenChange={setShowCreateModal}>
          <DialogContent className="bg-[#0A0A0A]/80 backdrop-blur-sm border-white/[0.08] text-white max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="font-heading text-xl uppercase">Create Contract Draft</DialogTitle>
            </DialogHeader>
            
            <div className="space-y-6 mt-4">
              {/* Template */}
              <div className="space-y-2">
                <Label className="text-white/70">Contract Template</Label>
                <Select value={createForm.template_type} onValueChange={handleTemplateSelect}>
                  <SelectTrigger className="bg-black border-white/[0.08] text-white"><SelectValue placeholder="Select template..." /></SelectTrigger>
                  <SelectContent className="bg-[#0A0A0A]/80 backdrop-blur-sm border-white/[0.08]">
                    {Object.entries(CONTRACT_TEMPLATES).map(([key, template]) => (
                      <SelectItem key={key} value={key} className="text-white hover:bg-white/[0.06]">{template.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Deal */}
              <div className="space-y-2">
                <Label className="text-white/70">Link to Deal (auto-fills data)</Label>
                <Select value={createForm.deal_id} onValueChange={handleDealSelect}>
                  <SelectTrigger className="bg-black border-white/[0.08] text-white"><SelectValue placeholder="Select deal..." /></SelectTrigger>
                  <SelectContent className="bg-[#0A0A0A]/80 backdrop-blur-sm border-white/[0.08]">
                    {deals.map((deal) => (
                      <SelectItem key={deal.deal_id} value={deal.deal_id} className="text-white hover:bg-white/[0.06]">{deal.property_address}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Contract Name */}
              <div className="space-y-2">
                <Label className="text-white/70">Contract Name</Label>
                <Input
                  value={createForm.name}
                  onChange={(e) => setCreateForm({...createForm, name: e.target.value})}
                  className="bg-black border-white/[0.08] text-white"
                  placeholder="e.g., 123 Main St - Purchase Agreement"
                />
              </div>

              {createForm.template_type && (
                <>
                  {/* Locked Fields Indicator */}
                  {createForm.deal_id && Object.keys(lockedFields).length > 0 && (
                    <div className="p-3 bg-gold/10 border border-gold/30 flex items-start gap-3">
                      <Lock className="w-5 h-5 text-gold flex-shrink-0 mt-0.5" />
                      <div>
                        <p className="text-gold font-medium text-sm">Deal Data Locked</p>
                        <p className="text-white/70 text-xs mt-1">
                          Fields like Property Address, Seller Name, and calculator values are pulled from the deal and cannot be edited here.
                          These ensure contract accuracy based on your analysis.
                        </p>
                      </div>
                    </div>
                  )}
                
                  {/* Parties */}
                  <div className="space-y-4">
                    <Label className="text-white/70 text-lg">Parties</Label>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label className="text-white/70 text-sm">Buyer Name *</Label>
                        <Input
                          value={createForm.fields.buyer_name || ""}
                          onChange={(e) => setCreateForm({...createForm, fields: {...createForm.fields, buyer_name: e.target.value}})}
                          className="bg-black border-white/[0.08] text-white"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label className="text-white/70 text-sm flex items-center gap-1">
                          Seller Name *
                          {lockedFields.seller_name && <Lock className="w-3 h-3 text-gold" />}
                        </Label>
                        <Input
                          value={lockedFields.seller_name || createForm.fields.seller_name || ""}
                          onChange={(e) => !lockedFields.seller_name && setCreateForm({...createForm, fields: {...createForm.fields, seller_name: e.target.value}})}
                          className={`${lockedFields.seller_name ? 'bg-gold/10 border-gold/30 text-gold cursor-not-allowed' : 'bg-black border-white/[0.08] text-white'}`}
                          readOnly={!!lockedFields.seller_name}
                        />
                      </div>
                    </div>
                  </div>

                  {/* Property (Locked) & Closing Date */}
                  {lockedFields.property_address && (
                    <div className="space-y-2">
                      <Label className="text-white/70 text-sm flex items-center gap-1">
                        Property Address <Lock className="w-3 h-3 text-gold" />
                      </Label>
                      <Input
                        value={lockedFields.property_address}
                        className="bg-gold/10 border-gold/30 text-gold cursor-not-allowed"
                        readOnly
                      />
                    </div>
                  )}

                  {/* Property & Price */}
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label className="text-white/70 text-sm flex items-center gap-1">
                        Purchase Price ($) *
                        {lockedFields.purchase_price && <Lock className="w-3 h-3 text-gold" />}
                      </Label>
                      <Input
                        value={lockedFields.purchase_price || createForm.fields.purchase_price || ""}
                        onChange={(e) => !lockedFields.purchase_price && setCreateForm({...createForm, fields: {...createForm.fields, purchase_price: e.target.value}})}
                        className={`${lockedFields.purchase_price ? 'bg-gold/10 border-gold/30 text-gold cursor-not-allowed' : 'bg-black border-white/[0.08] text-white'}`}
                        readOnly={!!lockedFields.purchase_price}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-white/70 text-sm">Closing Date *</Label>
                      <Input
                        type="date"
                        value={createForm.fields.closing_date || ""}
                        onChange={(e) => setCreateForm({...createForm, fields: {...createForm.fields, closing_date: e.target.value}})}
                        className="bg-black border-white/[0.08] text-white"
                      />
                    </div>
                  </div>

                  {/* EMD Section */}
                  <div className="space-y-4 p-4 bg-black border border-white/[0.08]">
                    <Label className="text-gold text-lg font-bold">Earnest Money Deposit (EMD)</Label>
                    <div className="grid grid-cols-3 gap-4">
                      <div className="space-y-2">
                        <Label className="text-white/70 text-sm">EMD Amount ($) *</Label>
                        <Input
                          value={createForm.fields.earnest_money || ""}
                          onChange={(e) => setCreateForm({...createForm, fields: {...createForm.fields, earnest_money: e.target.value}})}
                          className="bg-[#0A0A0A]/80 backdrop-blur-sm border-white/[0.1] text-white"
                          placeholder="5000"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label className="text-white/70 text-sm">Days to Deposit</Label>
                        <Input
                          value={createForm.fields.earnest_days || "3"}
                          onChange={(e) => setCreateForm({...createForm, fields: {...createForm.fields, earnest_days: e.target.value}})}
                          className="bg-[#0A0A0A]/80 backdrop-blur-sm border-white/[0.1] text-white"
                          placeholder="3"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label className="text-white/70 text-sm">Settlement Agent/Title Co</Label>
                        <Input
                          value={createForm.fields.escrow_agent || ""}
                          onChange={(e) => setCreateForm({...createForm, fields: {...createForm.fields, escrow_agent: e.target.value}})}
                          className="bg-[#0A0A0A]/80 backdrop-blur-sm border-white/[0.1] text-white"
                          placeholder="ABC Title Company"
                        />
                      </div>
                    </div>
                  </div>

                  {/* Inspection Period Section */}
                  <div className="space-y-4 p-4 bg-black border border-white/[0.08]">
                    <Label className="text-gold text-lg font-bold">Inspection Period</Label>
                    <div className="grid grid-cols-3 gap-4">
                      <div className="space-y-2">
                        <Label className="text-white/70 text-sm">Inspection Days *</Label>
                        <Input
                          type="number"
                          value={createForm.fields.inspection_days || "10"}
                          onChange={(e) => updateInspectionDates(e.target.value)}
                          className="bg-[#0A0A0A]/80 backdrop-blur-sm border-white/[0.1] text-white"
                          placeholder="10"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label className="text-white/70 text-sm">Start Date</Label>
                        <Input
                          type="date"
                          value={createForm.fields.inspection_start_date || ""}
                          onChange={(e) => {
                            const days = parseInt(createForm.fields.inspection_days || 10);
                            const start = new Date(e.target.value);
                            const end = new Date(start.getTime() + days * 24 * 60 * 60 * 1000);
                            setCreateForm({
                              ...createForm,
                              fields: {
                                ...createForm.fields,
                                inspection_start_date: e.target.value,
                                inspection_end_date: end.toISOString().split('T')[0]
                              }
                            });
                          }}
                          className="bg-[#0A0A0A]/80 backdrop-blur-sm border-white/[0.1] text-white"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label className="text-white/70 text-sm">End Date</Label>
                        <Input
                          type="date"
                          value={createForm.fields.inspection_end_date || ""}
                          readOnly
                          className="bg-[#0A0A0A]/80 backdrop-blur-sm border-white/[0.1] text-white/70"
                        />
                      </div>
                    </div>
                  </div>

                  {/* Creative Finance Fields */}
                  {["seller_finance", "wrap_agreement"].includes(createForm.template_type) && (
                    <div className="space-y-4 p-4 bg-black border border-white/[0.08]">
                      <Label className="text-gold text-lg font-bold">Financing Terms</Label>
                      <div className="grid grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label className="text-white/70 text-sm">Down Payment ($)</Label>
                          <Input
                            value={createForm.fields.down_payment || ""}
                            onChange={(e) => setCreateForm({...createForm, fields: {...createForm.fields, down_payment: e.target.value}})}
                            className="bg-[#0A0A0A]/80 backdrop-blur-sm border-white/[0.1] text-white"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label className="text-white/70 text-sm">Interest Rate (%)</Label>
                          <Input
                            value={createForm.fields.interest_rate || ""}
                            onChange={(e) => setCreateForm({...createForm, fields: {...createForm.fields, interest_rate: e.target.value}})}
                            className="bg-[#0A0A0A]/80 backdrop-blur-sm border-white/[0.1] text-white"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label className="text-white/70 text-sm">Amortization (years)</Label>
                          <Input
                            value={createForm.fields.amortization_years || ""}
                            onChange={(e) => setCreateForm({...createForm, fields: {...createForm.fields, amortization_years: e.target.value}})}
                            className="bg-[#0A0A0A]/80 backdrop-blur-sm border-white/[0.1] text-white"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label className="text-white/70 text-sm">Balloon (years)</Label>
                          <Input
                            value={createForm.fields.balloon_years || ""}
                            onChange={(e) => setCreateForm({...createForm, fields: {...createForm.fields, balloon_years: e.target.value}})}
                            className="bg-[#0A0A0A]/80 backdrop-blur-sm border-white/[0.1] text-white"
                          />
                        </div>
                      </div>
                    </div>
                  )}
                </>
              )}

              <div className="flex justify-end gap-3 pt-4 border-t border-white/[0.08]">
                <Button variant="outline" onClick={() => setShowCreateModal(false)} className="border-white/[0.1] text-white/70 hover:bg-white/[0.06] rounded-none">
                  Cancel
                </Button>
                <Button onClick={generateContract} disabled={!createForm.template_type} className="gold-gradient-btn rounded-none font-bold">
                  Generate Contract
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {/* Upload Template Modal */}
        <Dialog open={showUploadModal} onOpenChange={setShowUploadModal}>
          <DialogContent className="bg-[#0A0A0A]/80 backdrop-blur-sm border-white/[0.08] text-white max-w-md">
            <DialogHeader>
              <DialogTitle className="font-heading text-xl uppercase flex items-center gap-2">
                <Upload className="w-5 h-5 text-gold" />
                Upload Custom Template
              </DialogTitle>
            </DialogHeader>
            
            <div className="space-y-4 mt-4">
              <p className="text-white/70 text-sm">
                Upload a .docx file to use as a custom contract template. The document will be converted to an editable format.
              </p>
              
              <div className="border-2 border-dashed border-white/[0.1] rounded p-6 text-center hover:border-gold/50 transition-all duration-300">
                <input
                  type="file"
                  accept=".docx"
                  onChange={(e) => setUploadFile(e.target.files?.[0] || null)}
                  className="hidden"
                  id="template-upload"
                />
                <label htmlFor="template-upload" className="cursor-pointer">
                  {uploadFile ? (
                    <div className="space-y-2">
                      <FileUp className="w-12 h-12 text-gold mx-auto" />
                      <p className="text-white font-medium">{uploadFile.name}</p>
                      <p className="text-white/50 text-sm">{(uploadFile.size / 1024).toFixed(1)} KB</p>
                    </div>
                  ) : (
                    <div className="space-y-2">
                      <Upload className="w-12 h-12 text-white/40 mx-auto" />
                      <p className="text-white/70">Click to select a .docx file</p>
                      <p className="text-white/40 text-xs">Maximum 5MB</p>
                    </div>
                  )}
                </label>
              </div>
              
              <div className="bg-black border border-white/[0.08] p-3 text-xs text-white/50">
                <p className="font-medium text-white/70 mb-1">Supported Features:</p>
                <ul className="list-disc list-inside space-y-0.5">
                  <li>Headings and paragraphs</li>
                  <li>Bold, italic, underline text</li>
                  <li>Numbered and bulleted lists</li>
                  <li>Tables (basic formatting)</li>
                </ul>
              </div>
              
              <div className="flex justify-end gap-3 pt-4 border-t border-white/[0.08]">
                <Button 
                  variant="outline" 
                  onClick={() => { setShowUploadModal(false); setUploadFile(null); }} 
                  className="border-white/[0.1] text-white/70 hover:bg-white/[0.06] rounded-none"
                >
                  Cancel
                </Button>
                <Button 
                  onClick={handleFileUpload}
                  disabled={uploading || !uploadFile}
                  className="gold-gradient-btn rounded-none font-bold"
                >
                  {uploading ? (
                    <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Uploading...</>
                  ) : (
                    <><Upload className="w-4 h-4 mr-2" />Upload & Edit</>
                  )}
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {/* Signature Request Modal */}
        <Dialog open={showSignatureModal} onOpenChange={setShowSignatureModal}>
          <DialogContent className="bg-[#0A0A0A]/80 backdrop-blur-sm border-white/[0.08] text-white max-w-md">
            <DialogHeader>
              <DialogTitle className="font-heading text-xl uppercase flex items-center gap-2">
                <Send className="w-5 h-5 text-[#00FF88] drop-shadow-[0_0_10px_rgba(0,255,136,0.5)]" />
                Send for Signature
              </DialogTitle>
            </DialogHeader>
            
            <div className="space-y-4 mt-4">
              <p className="text-white/70 text-sm">
                Send this contract to a signer via email. They will receive a link to review and sign the document electronically.
              </p>
              
              <div className="space-y-2">
                <Label className="text-white/70">Signer's Name *</Label>
                <Input
                  value={signatureForm.signer_name}
                  onChange={(e) => setSignatureForm({...signatureForm, signer_name: e.target.value})}
                  placeholder="John Doe"
                  className="bg-black border-white/[0.08] text-white"
                />
              </div>
              
              <div className="space-y-2">
                <Label className="text-white/70">Signer's Email *</Label>
                <Input
                  type="email"
                  value={signatureForm.signer_email}
                  onChange={(e) => setSignatureForm({...signatureForm, signer_email: e.target.value})}
                  placeholder="john@example.com"
                  className="bg-black border-white/[0.08] text-white"
                />
              </div>
              
              <div className="space-y-2">
                <Label className="text-white/70">Signer Role</Label>
                <Select 
                  value={signatureForm.signer_role} 
                  onValueChange={(val) => setSignatureForm({...signatureForm, signer_role: val})}
                >
                  <SelectTrigger className="bg-black border-white/[0.08] text-white">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-[#0A0A0A]/80 backdrop-blur-sm border-white/[0.08]">
                    <SelectItem value="buyer" className="text-white hover:bg-white/[0.06]">Buyer</SelectItem>
                    <SelectItem value="seller" className="text-white hover:bg-white/[0.06]">Seller</SelectItem>
                    <SelectItem value="agent" className="text-white hover:bg-white/[0.06]">Agent</SelectItem>
                    <SelectItem value="witness" className="text-white hover:bg-white/[0.06]">Witness</SelectItem>
                    <SelectItem value="other" className="text-white hover:bg-white/[0.06]">Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Existing signatures */}
              {contractSignatures.length > 0 && (
                <div className="bg-black border border-white/[0.08] p-3">
                  <p className="text-white/70 text-xs uppercase font-bold mb-2">
                    Current Signature Requests
                  </p>
                  <div className="space-y-2">
                    {contractSignatures.map((sig) => (
                      <div key={sig.signature_id} className="flex items-center justify-between text-sm">
                        <div>
                          <span className="text-white">{sig.signer_name}</span>
                          <span className="text-white/70 ml-2">({sig.signer_role})</span>
                        </div>
                        <span className={`text-xs px-2 py-0.5 ${
                          sig.status === 'signed' ? 'bg-emerald-500/20 text-[#00FF88] drop-shadow-[0_0_10px_rgba(0,255,136,0.5)]' :
                          sig.status === 'declined' ? 'bg-red-500/20 text-[#FF3B30]' :
                          'bg-amber-500/20 text-[#FF9F0A] drop-shadow-[0_0_8px_rgba(255,159,10,0.5)]'
                        }`}>
                          {sig.status}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              
              <div className="flex justify-end gap-3 pt-4 border-t border-white/[0.08]">
                <Button 
                  variant="outline" 
                  onClick={() => setShowSignatureModal(false)} 
                  className="border-white/[0.1] text-white/70 hover:bg-white/[0.06] rounded-none"
                >
                  Cancel
                </Button>
                <Button 
                  onClick={sendSignatureRequest} 
                  disabled={sendingSignature || !signatureForm.signer_name || !signatureForm.signer_email}
                  className="bg-emerald-600 hover:bg-emerald-700 text-white rounded-none font-bold"
                >
                  {sendingSignature ? (
                    <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Sending...</>
                  ) : (
                    <><Mail className="w-4 h-4 mr-2" />Send Request</>
                  )}
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {/* ─── AI Contract Generator Modal ─────────────────────────────────────── */}
        {showAIModal && (
          <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4">
            <div className="bg-[#0A0A0A] border border-white/[0.08] rounded-xl w-full max-w-xl max-h-[90vh] overflow-y-auto">
              <div className="flex items-center justify-between p-6 border-b border-white/[0.06]">
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 bg-purple-500/10 border border-purple-500/20 rounded-lg flex items-center justify-center">
                    <Sparkles className="w-4 h-4 text-[#BF5AF2]" />
                  </div>
                  <div>
                    <h2 className="text-white font-bold">AI Contract Generator</h2>
                    <p className="text-white/30 text-xs">Llama 3.3 70B — Full contract draft in seconds</p>
                  </div>
                </div>
                <button onClick={() => setShowAIModal(false)} className="text-white/40 hover:text-white transition-colors">
                  <X className="w-5 h-5" />
                </button>
              </div>

              <div className="p-6 space-y-4">
                {/* Contract Type */}
                <div className="space-y-1.5">
                  <label className="text-white/60 text-xs font-medium">Contract Type *</label>
                  <div className="grid grid-cols-2 gap-2">
                    {[
                      { key: "purchase_agreement", label: "Purchase Agreement" },
                      { key: "assignment", label: "Assignment" },
                      { key: "lease_option", label: "Lease Option" },
                      { key: "letter_of_intent", label: "Letter of Intent" },
                    ].map(t => (
                      <button key={t.key}
                        onClick={() => setAIForm(f => ({ ...f, contract_type: t.key }))}
                        className={`px-3 py-2 text-xs font-medium rounded-lg border transition-all ${
                          aiForm.contract_type === t.key
                            ? "bg-purple-500/10 border-purple-500/30 text-[#BF5AF2]"
                            : "border-white/[0.06] text-white/40 hover:text-white"
                        }`}>
                        {t.label}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Parties */}
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1.5">
                    <label className="text-white/60 text-xs font-medium">Buyer / Assignee *</label>
                    <input
                      value={aiForm.buyer_name}
                      onChange={e => setAIForm(f => ({ ...f, buyer_name: e.target.value }))}
                      placeholder="John Smith"
                      className="w-full bg-black border border-white/[0.08] rounded-lg px-3 py-2 text-sm text-white placeholder:text-white/20 focus:outline-none focus:border-purple-500/40"
                      data-testid="ai-buyer-name"
                    />
                  </div>
                  <div className="space-y-1.5">
                    <label className="text-white/60 text-xs font-medium">Seller / Assignor *</label>
                    <input
                      value={aiForm.seller_name}
                      onChange={e => setAIForm(f => ({ ...f, seller_name: e.target.value }))}
                      placeholder="Jane Doe"
                      className="w-full bg-black border border-white/[0.08] rounded-lg px-3 py-2 text-sm text-white placeholder:text-white/20 focus:outline-none focus:border-purple-500/40"
                      data-testid="ai-seller-name"
                    />
                  </div>
                </div>

                {/* Property */}
                <div className="space-y-1.5">
                  <label className="text-white/60 text-xs font-medium">Property Address *</label>
                  <input
                    value={aiForm.property_address}
                    onChange={e => setAIForm(f => ({ ...f, property_address: e.target.value }))}
                    placeholder="123 Main St, Dallas TX 75201"
                    className="w-full bg-black border border-white/[0.08] rounded-lg px-3 py-2 text-sm text-white placeholder:text-white/20 focus:outline-none focus:border-purple-500/40"
                    data-testid="ai-property-address"
                  />
                </div>

                {/* Financials */}
                <div className="grid grid-cols-3 gap-3">
                  <div className="space-y-1.5">
                    <label className="text-white/60 text-xs font-medium">Purchase Price ($)</label>
                    <input type="number" value={aiForm.purchase_price}
                      onChange={e => setAIForm(f => ({ ...f, purchase_price: e.target.value }))}
                      placeholder="200000"
                      className="w-full bg-black border border-white/[0.08] rounded-lg px-3 py-2 text-sm text-white font-mono placeholder:text-white/20 focus:outline-none focus:border-purple-500/40" />
                  </div>
                  {aiForm.contract_type === "assignment" ? (
                    <div className="space-y-1.5">
                      <label className="text-white/60 text-xs font-medium">Assignment Fee ($)</label>
                      <input type="number" value={aiForm.assignment_fee}
                        onChange={e => setAIForm(f => ({ ...f, assignment_fee: e.target.value }))}
                        placeholder="10000"
                        className="w-full bg-black border border-white/[0.08] rounded-lg px-3 py-2 text-sm text-white font-mono placeholder:text-white/20 focus:outline-none focus:border-purple-500/40" />
                    </div>
                  ) : (
                    <div className="space-y-1.5">
                      <label className="text-white/60 text-xs font-medium">Earnest Money ($)</label>
                      <input type="number" value={aiForm.earnest_money}
                        onChange={e => setAIForm(f => ({ ...f, earnest_money: e.target.value }))}
                        placeholder="5000"
                        className="w-full bg-black border border-white/[0.08] rounded-lg px-3 py-2 text-sm text-white font-mono placeholder:text-white/20 focus:outline-none focus:border-purple-500/40" />
                    </div>
                  )}
                  <div className="space-y-1.5">
                    <label className="text-white/60 text-xs font-medium">Closing Date</label>
                    <input type="date" value={aiForm.closing_date}
                      onChange={e => setAIForm(f => ({ ...f, closing_date: e.target.value }))}
                      className="w-full bg-black border border-white/[0.08] rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-purple-500/40" />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1.5">
                    <label className="text-white/60 text-xs font-medium">Inspection Days</label>
                    <input type="number" value={aiForm.inspection_days}
                      onChange={e => setAIForm(f => ({ ...f, inspection_days: e.target.value }))}
                      placeholder="10"
                      className="w-full bg-black border border-white/[0.08] rounded-lg px-3 py-2 text-sm text-white font-mono placeholder:text-white/20 focus:outline-none focus:border-purple-500/40" />
                  </div>
                  <div className="space-y-1.5">
                    <label className="text-white/60 text-xs font-medium">State</label>
                    <input value={aiForm.state}
                      onChange={e => setAIForm(f => ({ ...f, state: e.target.value }))}
                      placeholder="Texas"
                      className="w-full bg-black border border-white/[0.08] rounded-lg px-3 py-2 text-sm text-white placeholder:text-white/20 focus:outline-none focus:border-purple-500/40" />
                  </div>
                </div>

                <div className="space-y-1.5">
                  <label className="text-white/60 text-xs font-medium">Special Terms (optional)</label>
                  <textarea value={aiForm.special_terms}
                    onChange={e => setAIForm(f => ({ ...f, special_terms: e.target.value }))}
                    placeholder="Any special conditions, contingencies, or deal-specific terms…"
                    rows={2}
                    className="w-full bg-black border border-white/[0.08] rounded-lg px-3 py-2 text-sm text-white placeholder:text-white/20 focus:outline-none focus:border-purple-500/40 resize-none" />
                </div>

                <div className="p-3 bg-amber-500/[0.06] border border-amber-500/20 rounded-lg">
                  <p className="text-[#FF9F0A] text-xs font-semibold">AI Draft — Not Legal Advice</p>
                  <p className="text-white/40 text-xs mt-0.5">Always have a licensed attorney review before execution. Laws vary by state.</p>
                </div>

                <div className="flex gap-3">
                  <button onClick={() => setShowAIModal(false)}
                    className="flex-1 py-2.5 border border-white/[0.08] text-white/60 hover:text-white rounded-lg text-sm transition-all">
                    Cancel
                  </button>
                  <button
                    onClick={generateAIContract}
                    disabled={aiGenerating || !aiForm.buyer_name || !aiForm.seller_name || !aiForm.property_address}
                    data-testid="ai-generate-submit-btn"
                    className="flex-1 py-2.5 bg-[#BF5AF2] hover:bg-[#BF5AF2]/90 disabled:opacity-50 text-white font-bold rounded-lg text-sm transition-all flex items-center justify-center gap-2">
                    {aiGenerating ? (
                      <><RefreshCw className="w-4 h-4 animate-spin" /> Generating…</>
                    ) : (
                      <><Sparkles className="w-4 h-4" /> Generate Contract</>
                    )}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}
